import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Badge,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  ListItemText,
  Tabs,
  Tab,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddTaskIcon from '@mui/icons-material/AddTask';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PeopleIcon from '@mui/icons-material/People';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  productService,
  productionOrderService,
  operatorShiftReportService,
  orderMaterialDryingService,
  machineBreakService,
  maintenancePlanService,
  productionReportService,
  machineService,
  pressFormService,
  rawMaterialService,
  dyeService,
  stockItemService,
  qualityCheckService,
  customerOrderService,
  staffService,
  roleService,
  mixingBatchStockService,
  setupParameterHistoryService,
  authService,
} from '../services/api';
import { orderRefFromRow } from '../utils/orderLabel';

const STATUS_LABELS = {
  planned: 'Заплановано',
  in_progress: 'В роботі',
  completed: 'Завершено',
  cancelled: 'Скасовано',
};

const DRYING_STATUS_LABELS = {
  pending: 'Очікує',
  in_progress: 'В процесі',
  completed: 'Завершено',
  cancelled: 'Скасовано',
};

function formatDt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toIso(d) {
  if (!d) return '';
  // datetime-local очікує локальний час без timezone; не можна використовувати toISOString() (UTC)
  const x = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = x.getFullYear();
  const mm = pad(x.getMonth() + 1);
  const dd = pad(x.getDate());
  const hh = pad(x.getHours());
  const mi = pad(x.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function defaultPlanPermol() {
  return { raw_material_id: '', quantity_kg: '', mode: 'kg', pct: '' };
}

/** Скільки кг перемолу з полів форми (кг або % від загальної маси замовлення). */
function parsePermolKgInput(permol, totalKg) {
  if (!permol?.raw_material_id) return 0;
  if (permol.mode === 'pct') {
    const p = parseFloat(String(permol.pct ?? '').replace(',', '.'));
    if (!Number.isFinite(p) || p <= 0) return 0;
    return (totalKg * Math.min(p, 100)) / 100;
  }
  const k = parseFloat(String(permol.quantity_kg ?? '').replace(',', '.'));
  return Number.isFinite(k) && k > 0 ? k : 0;
}

/** Склади для призначення випуску (як у замовленні клієнта; без порожнього значення). */
const PLANNER_DESTINATION_WAREHOUSES = [
  'Склад напівфабрикатів',
  'Склад для пакування',
  'Склад готової продукції',
  'Склад сировини',
  'Склад упаковки',
  'Склад барвників',
  'Склад техматеріалів',
];

const GANTT_MIN_HEIGHT = 200;
const GANTT_MAX_HEIGHT = 900;
const GANTT_DEFAULT_HEIGHT = 400;
const GANTT_BASE_TIME_AXIS_H = 28;
const GANTT_BASE_ROW_H = 36;
const GANTT_BASE_BAR_H = 24;
const GANTT_LEFT_COL_PX = 140;
// ----- Gantt -----
const GanttBlock = React.memo(function GanttBlock({
  orders,
  machineBreaks,
  maintenancePlans,
  machines,
  zoomDays,
  height = GANTT_DEFAULT_HEIGHT,
  onHeightChange,
  scrollToTodayOnMount = true,
}) {
  const scrollRef = useRef(null);
  const [innerHeight, setInnerHeight] = useState(height);

  const allTimeItems = useMemo(() => {
    const list = [];
    orders.forEach((o) => {
      if (!o.planned_start || !o.planned_end) return;
      list.push({
        type: 'order',
        id: o.id,
        machineId: typeof o.machine === 'object' ? o.machine.id : o.machine,
        start: new Date(o.planned_start).getTime(),
        end: new Date(o.planned_end).getTime(),
        payload: o,
      });
    });
    (machineBreaks || []).forEach((b) => {
      if (!b.start_at || !b.end_at) return;
      list.push({
        type: 'break',
        id: `break-${b.id}`,
        machineId: b.machine,
        start: new Date(b.start_at).getTime(),
        end: new Date(b.end_at).getTime(),
        payload: b,
      });
    });
    (maintenancePlans || []).forEach((m) => {
      if (!m.start_at || !m.end_at) return;
      list.push({
        type: 'maintenance',
        id: `mnt-${m.id}`,
        machineId: m.machine,
        start: new Date(m.start_at).getTime(),
        end: new Date(m.end_at).getTime(),
        payload: m,
      });
    });
    return list;
  }, [orders, machineBreaks, maintenancePlans]);

  const nowMs = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  const fallbackStart = nowMs - 30 * dayMs;
  const fallbackEnd = nowMs + 30 * dayMs;
  const minTime =
    allTimeItems.length > 0 ? Math.min(...allTimeItems.map((i) => i.start)) - 12 * 60 * 60 * 1000 : fallbackStart;
  const maxTime =
    allTimeItems.length > 0 ? Math.max(...allTimeItems.map((i) => i.end)) + 12 * 60 * 60 * 1000 : fallbackEnd;

  const rangeStart = minTime;
  const rangeEnd = maxTime;
  const rangeMs = Math.max(rangeEnd - rangeStart, 60 * 60 * 1000);
  const rangeHours = rangeMs / (1000 * 60 * 60);

  const zoomPxPerHour =
    zoomDays <= 1 ? 40 : zoomDays <= 3 ? 24 : zoomDays <= 7 ? 16 : zoomDays <= 30 ? 8 : 4;
  const timelineWidthPx = Math.max(800, Math.ceil(rangeHours * zoomPxPerHour));

  const nowLeftPx = ((nowMs - rangeStart) / rangeMs) * timelineWidthPx;

  const scaleY = innerHeight / GANTT_DEFAULT_HEIGHT;
  const timeAxisH = Math.round(GANTT_BASE_TIME_AXIS_H * scaleY);
  const rowH = Math.round(GANTT_BASE_ROW_H * scaleY);
  const barH = Math.round(GANTT_BASE_BAR_H * scaleY);
  const leftColPx = GANTT_LEFT_COL_PX;

  const [detailItem, setDetailItem] = useState(null);

  const machineIds = useMemo(
    () =>
      [...(machines || [])].sort((a, b) =>
        (a.identifier || '').localeCompare(b.identifier || '', undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      ),
    [machines]
  );

  const timeLabels = useMemo(() => {
    const labels = [];
    const endDate = new Date(rangeEnd);

    if (zoomDays <= 1) {
      // Один день: дата + час з кроком 3 години
      const stepHours = 3;
      let t = new Date(rangeStart);
      t.setMinutes(0, 0, 0);
      if (t.getTime() < rangeStart) t.setHours(t.getHours() + stepHours);
      while (t.getTime() <= rangeEnd) {
        const x = ((t.getTime() - rangeStart) / rangeMs) * timelineWidthPx;
        const isMidnight = t.getHours() === 0 && t.getMinutes() === 0;
        const label = isMidnight
          ? t.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' })
          : t.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
        labels.push({ x, label });
        t.setHours(t.getHours() + stepHours);
      }
    } else {
      // Більший масштаб: мітки лише на початок доби, лише дата
      let t = new Date(rangeStart);
      t.setHours(0, 0, 0, 0);
      const stepDays = zoomDays <= 7 ? 1 : 7;
      if (t.getTime() < rangeStart) t.setTime(t.getTime() + stepDays * dayMs);
      while (t.getTime() <= rangeEnd) {
        const x = ((t.getTime() - rangeStart) / rangeMs) * timelineWidthPx;
        labels.push({
          x,
          label: t.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' }),
        });
        t.setTime(t.getTime() + dayMs);
      }
    }
    return labels;
  }, [rangeStart, rangeEnd, rangeMs, rangeHours, timelineWidthPx]);

  // Центрація по лінії «сьогодні» (якщо є горизонтальний скрол)
  useEffect(() => {
    if (!scrollToTodayOnMount || !scrollRef.current) return;
    const el = scrollRef.current;
    const totalW = leftColPx + timelineWidthPx;
    if (totalW <= el.clientWidth) return;
    const targetScroll = leftColPx + nowLeftPx - el.clientWidth / 2;
    const next = Math.max(0, Math.min(targetScroll, totalW - el.clientWidth));
    const raf = requestAnimationFrame(() => { el.scrollLeft = next; });
    return () => cancelAnimationFrame(raf);
  }, [scrollToTodayOnMount, rangeStart, rangeEnd, nowLeftPx, timelineWidthPx]);

  const handleResizeMouseDown = (e) => {
    if (!onHeightChange || e.button !== 0) return;
    e.preventDefault();
    const startY = e.clientY;
    const startH = innerHeight;
    const onMove = (e2) => {
      const dy = e2.clientY - startY;
      const next = Math.round(Math.min(GANTT_MAX_HEIGHT, Math.max(GANTT_MIN_HEIGHT, startH + dy)));
      setInnerHeight(next);
      onHeightChange && onHeightChange(next);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', height: height, display: 'flex', flexDirection: 'column', minHeight: GANTT_MIN_HEIGHT }}>
      <Box ref={scrollRef} sx={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
        <Box sx={{ minWidth: leftColPx + timelineWidthPx, width: leftColPx + timelineWidthPx }}>
          {/* Ось часу з мітками */}
          <Box sx={{ display: 'flex', height: timeAxisH, borderBottom: 1, borderColor: 'divider', position: 'relative' }}>
            <Box
              sx={{
                width: leftColPx,
                minWidth: leftColPx,
                flexShrink: 0,
                position: 'sticky',
                left: 0,
                zIndex: 5,
                bgcolor: 'background.paper',
                borderRight: 1,
                borderColor: 'divider',
              }}
            />
            <Box sx={{ position: 'relative', width: timelineWidthPx, height: '100%', bgcolor: 'action.hover' }}>
              {timeLabels.map(({ x, label }, idx) => (
                <Box
                  key={`t-${idx}-${x}`}
                  sx={{
                    position: 'absolute',
                    left: x,
                    transform: 'translateX(-50%)',
                    fontSize: `${Math.max(10, 0.7 * 16 * scaleY)}px`,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Box>
              ))}
              {/* Лінія «сьогодні» */}
              {nowLeftPx >= 0 && nowLeftPx <= timelineWidthPx && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: nowLeftPx,
                    top: 0,
                    bottom: 0,
                    width: Math.max(2, 2 * scaleY),
                    bgcolor: 'error.main',
                    zIndex: 2,
                    '&::before': {
                      content: '"Сьогодні"',
                      position: 'absolute',
                      top: -2,
                      left: 2,
                      fontSize: `${Math.max(10, 0.65 * 16 * scaleY)}px`,
                      color: 'error.main',
                      fontWeight: 600,
                    },
                  }}
                  title={formatDt(new Date(nowMs))}
                />
              )}
            </Box>
          </Box>
          {/* Рядки станків */}
          {machineIds.map((machine) => {
            const mid = Number(machine.id);
            const rowItems = allTimeItems.filter((i) => Number(i.machineId) === mid);
            return (
              <Box
                key={machine.id}
                sx={{ display: 'flex', alignItems: 'center', height: rowH, borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Box
                  sx={{
                    width: leftColPx,
                    minWidth: leftColPx,
                    flexShrink: 0,
                    position: 'sticky',
                    left: 0,
                    zIndex: 5,
                    bgcolor: 'background.paper',
                    borderRight: 1,
                    borderColor: 'divider',
                    fontSize: `${Math.max(12, 0.85 * 16 * scaleY)}px`,
                    pl: 0.5,
                  }}
                >
                  {machine.identifier} — {machine.name}
                </Box>
                <Box
                  sx={{
                    position: 'relative',
                    width: timelineWidthPx,
                    height: rowH - 8,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                  }}
                >
                  {rowItems.map((item) => {
                    const leftPx = ((item.start - rangeStart) / rangeMs) * timelineWidthPx;
                    const widthPx = Math.max(16, ((item.end - item.start) / rangeMs) * timelineWidthPx);
                    let bgcolor = 'primary.light';
                    if (item.type === 'order') {
                      const st = item.payload.status;
                      if (st === 'completed') bgcolor = 'success.main';
                      else if (st === 'in_progress') bgcolor = 'primary.main';
                      else if (st === 'cancelled') bgcolor = 'grey.500';
                      else bgcolor = 'primary.light';
                    } else if (item.type === 'break') {
                      bgcolor = 'warning.main';
                    } else if (item.type === 'maintenance') {
                      bgcolor = 'secondary.main';
                    }
                    const topOffset =
                      item.type === 'order' ? (rowH - 8 - barH) / 2 : item.type === 'break' ? 2 : rowH / 2;
                    const h = item.type === 'order' ? barH : Math.max(6, barH * 0.6);
                    const label =
                      item.type === 'order'
                        ? `${item.payload.product_name} x${item.payload.quantity}`
                        : item.type === 'break'
                        ? `Перерва: ${item.payload.reason || ''}`
                        : `Обслуговування`;
                    return (
                      <Box
                        key={item.id}
                        onClick={() => setDetailItem(item)}
                        sx={{
                          position: 'absolute',
                          left: Math.max(0, leftPx),
                          width: Math.min(widthPx, timelineWidthPx - Math.max(0, leftPx)),
                          minWidth: 16,
                          height: h,
                          top: topOffset,
                          zIndex: item.type === 'order' ? 1 : 2,
                          bgcolor,
                          color: 'primary.contrastText',
                          borderRadius: 999,
                          border: '1px solid',
                          borderColor: 'rgba(0,0,0,0.25)',
                          boxShadow: '0 1px 0 rgba(0,0,0,0.12)',
                          fontSize: `${Math.max(10, 0.7 * 16 * scaleY)}px`,
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          pl: 0.5,
                          cursor: 'pointer',
                          opacity: item.type === 'cancelled' ? 0.6 : 1,
                        }}
                        title={label}
                      >
                        {label}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            );
          })}
          {allTimeItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 * scaleY, pl: 2, fontSize: `${Math.max(12, 14 * scaleY)}px` }}>
              Немає запланованих замовлень. Заплануйте замовлення, щоб побачити діаграму.
            </Typography>
          )}
        </Box>
      </Box>
      {onHeightChange && (
        <Tooltip title="Розтягнути по висоті">
          <Box
            onMouseDown={handleResizeMouseDown}
            sx={{
              height: 8,
              cursor: 'ns-resize',
              bgcolor: 'action.hover',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <Box sx={{ width: 40, height: 2, borderRadius: 1, bgcolor: 'divider' }} />
          </Box>
        </Tooltip>
      )}

      {/* Деталі елемента діаграми */}
      <Dialog
        open={Boolean(detailItem)}
        onClose={() => setDetailItem(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Деталі</DialogTitle>
        <DialogContent dividers>
          {detailItem && detailItem.type === 'order' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2">{detailItem.payload.product_name}</Typography>
              <Typography variant="body2">Кількість: {detailItem.payload.quantity}</Typography>
              <Typography variant="body2">Станок: {detailItem.payload.machine_identifier}</Typography>
              <Typography variant="body2">
                План: {formatDt(detailItem.payload.planned_start)} — {formatDt(detailItem.payload.planned_end)}
              </Typography>
              <Typography variant="body2">Статус: {STATUS_LABELS[detailItem.payload.status] || detailItem.payload.status}</Typography>
            </Box>
          )}
          {detailItem && detailItem.type === 'break' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2">Перерва</Typography>
              <Typography variant="body2">
                Період: {formatDt(detailItem.payload.start_at)} — {formatDt(detailItem.payload.end_at)}
              </Typography>
              <Typography variant="body2">Причина: {detailItem.payload.reason}</Typography>
            </Box>
          )}
          {detailItem && detailItem.type === 'maintenance' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2">Планове обслуговування</Typography>
              <Typography variant="body2">
                Період: {formatDt(detailItem.payload.start_at)} — {formatDt(detailItem.payload.end_at)}
              </Typography>
              {detailItem.payload.description && (
                <Typography variant="body2">Опис: {detailItem.payload.description}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailItem(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

function getStoredUser() {
  try {
    const raw = localStorage.getItem('currentUser');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function ProductionPage({ mode = 'planner', view = 'default', currentUser: currentUserProp }) {
  const isDryingView = view === 'drying';
  const currentUser = currentUserProp || (mode === 'operator' ? getStoredUser() : null);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderQueryParam = searchParams.get('order');
  const location = useLocation();

  const [orders, setOrders] = useState([]);
  const [machineBreaks, setMachineBreaks] = useState([]);
  const [maintenancePlans, setMaintenancePlans] = useState([]);
  const [machines, setMachines] = useState([]);
  const [products, setProducts] = useState([]);
  const [pressForms, setPressForms] = useState([]);
  const [rawMaterials, setRawMaterials] = useState([]);
  const [dyes, setDyes] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(() => {
    const base = {
      status: '',
      planned_start_after: '',
      planned_start_before: '',
      product_name: '',
      machine: '',
      order_id: '',
    };
    try {
      const o = new URLSearchParams(window.location.search).get('order');
      if (o && /^\d+$/.test(o)) base.order_id = o;
    } catch {
      /* ignore */
    }
    return base;
  });

  const [planOrderOpen, setPlanOrderOpen] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [planAltMaterials, setPlanAltMaterials] = useState({});
  const [planOrderTab, setPlanOrderTab] = useState(0); // 0 = з замовлень, 1 = нове
  const [addBreakOpen, setAddBreakOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [startLateOpen, setStartLateOpen] = useState(false);
  const [endLateOpen, setEndLateOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [packagingWarn, setPackagingWarn] = useState(null); // { order, stage, detail, items, startPayload }

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '' });
  const [planForm, setPlanForm] = useState({
    customer_order: '',
    customer_order_line: '',
    product: '',
    quantity: '',
    config_id: '',
    planned_start: '',
    comment: '',
    materials: [],
    packaging_rows: [],
    permol: defaultPlanPermol(),
    destination_warehouse: 'Склад готової продукції',
    assigned_operator: '',
    mixing_batch_stock_id: '',
    needs_drying: false,
    drying_time_minutes: '',
  });
  const [availableBatches, setAvailableBatches] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [packagingStock, setPackagingStock] = useState([]);
  const [stockItemsAll, setStockItemsAll] = useState([]);
  const [permolStockList, setPermolStockList] = useState([]);
  const [permolDetailOpen, setPermolDetailOpen] = useState(null);
  const [historyWeightKg, setHistoryWeightKg] = useState(null);
  const [historyWeightMeta, setHistoryWeightMeta] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [breakForm, setBreakForm] = useState({ machine: '', start_at: '', end_at: '', reason: '' });
  const [reportLines, setReportLines] = useState([]);
  const [startLateReason, setStartLateReason] = useState('');
  const [endLateReason, setEndLateReason] = useState('');
  const [completeOrderLate, setCompleteOrderLate] = useState(false);
  const [crushingWasteKg, setCrushingWasteKg] = useState('0');
  const [nonRecoverableWasteKg, setNonRecoverableWasteKg] = useState('');
  const [setupWastes, setSetupWastes] = useState([]);

  const [qcTriggerOpen, setQcTriggerOpen] = useState(false);
  const [qcTriggerMachines, setQcTriggerMachines] = useState([]);
  const [qcTriggerOperator, setQcTriggerOperator] = useState('');
  const [qcTriggerSubmitting, setQcTriggerSubmitting] = useState(false);
  const [pendingQcChecks, setPendingQcChecks] = useState([]);
  const [currentQcCheck, setCurrentQcCheck] = useState(null);
  const [qcCompleteComment, setQcCompleteComment] = useState('');
  const [qcCompletePassed, setQcCompletePassed] = useState(true);
  const [qcCompleteSubmitting, setQcCompleteSubmitting] = useState(false);
  /** Немає відкритих перевірок — показуємо підказку + кнопку створити */
  const [qcNoPendingOpen, setQcNoPendingOpen] = useState(false);
  const [qcNoPendingSubmitting, setQcNoPendingSubmitting] = useState(false);

  const [operatorMachinesOpen, setOperatorMachinesOpen] = useState(false);
  const [operatorMachinesAssignments, setOperatorMachinesAssignments] = useState({});
  const [operatorMachinesSaving, setOperatorMachinesSaving] = useState(false);
  const [operatorUserFetched, setOperatorUserFetched] = useState(null);

  // Оператори для вибраного ТПА в діалозі планування
  const [machineOperators, setMachineOperators] = useState([]);
  const [machineOperatorsLoading, setMachineOperatorsLoading] = useState(false);

  // Попап призначення замовлення (режим оператора)
  const [assignedOrderPopup, setAssignedOrderPopup] = useState(null);
  const [pendingAssignments, setPendingAssignments] = useState([]);
  const [declineReasonInput, setDeclineReasonInput] = useState('');
  const [declineMode, setDeclineMode] = useState(false);
  const [popupSubmitting, setPopupSubmitting] = useState(false);

  // Звіт за зміну (режим оператора)
  const [shiftReportOpen, setShiftReportOpen] = useState(false);
  const [shiftReportLines, setShiftReportLines] = useState([]);
  const [shiftReportStep, setShiftReportStep] = useState(0);
  const [shiftReportSubmitting, setShiftReportSubmitting] = useState(false);
  const [shiftReportDone, setShiftReportDone] = useState(false);

  // Оператори зміни (режим планера)
  const [shiftOpsOpen, setShiftOpsOpen] = useState(false);
  const [shiftOpsOrders, setShiftOpsOrders] = useState([]);
  const [shiftOpsAssignments, setShiftOpsAssignments] = useState({});
  const [shiftOpsOperators, setShiftOpsOperators] = useState({});
  const [shiftOpsSaving, setShiftOpsSaving] = useState(false);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [dryingRows, setDryingRows] = useState([]);
  const [dryingLoading, setDryingLoading] = useState(false);
  const [dryingActiveOnly, setDryingActiveOnly] = useState(true);
  const [dryingBusyId, setDryingBusyId] = useState(null);

  const loadOrders = async () => {
    const paramsBase = { ordering: '-created_at' };
    if (filters.status) paramsBase.status = filters.status;
    if (filters.machine) paramsBase.machine = filters.machine;
    if (filters.product_name) paramsBase.product_name = filters.product_name;
    if (filters.planned_start_after) paramsBase.planned_start_after = filters.planned_start_after;
    if (filters.planned_start_before) paramsBase.planned_start_before = filters.planned_start_before;
    if (filters.order_id) paramsBase.id = filters.order_id;
    const acc = [];
    let page = 1;
    const maxPages = 500;
    for (;;) {
      const res = await productionOrderService.getAll({ ...paramsBase, page });
      const data = res.data;
      const chunk = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];
      acc.push(...chunk);
      if (!data?.next || chunk.length === 0 || page >= maxPages) break;
      page += 1;
    }
    const createdTs = (o) => (o.created_at ? new Date(o.created_at).getTime() : 0);
    acc.sort((a, b) => {
      const tb = createdTs(b);
      const ta = createdTs(a);
      if (tb !== ta) return tb - ta;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
    setOrders(acc);
  };

  const userForOperator = operatorUserFetched || currentUser;

  const operatorMachineIds = useMemo(() => {
    if (!userForOperator || mode !== 'operator') return [];
    const data = userForOperator.machines_data || userForOperator.machines || [];
    if (!Array.isArray(data)) return [];
    const ids = data.map((m) => (typeof m === 'object' && m != null && 'id' in m ? m.id : m)).filter((id) => id != null && id !== '');
    return ids.map((id) => Number(id));
  }, [userForOperator, mode]);

  // У режимі оператора: якщо в currentUser немає машин (наприклад застарілий кеш), підтягуємо профіль з API
  useEffect(() => {
    if (mode !== 'operator' || !currentUser?.id || operatorUserFetched) return;
    const hasMachines =
      (Array.isArray(currentUser.machines_data) && currentUser.machines_data.length > 0) ||
      (Array.isArray(currentUser.machines) && currentUser.machines.length > 0);
    if (hasMachines) return;
    let cancelled = false;
    staffService
      .getById(currentUser.id)
      .then((res) => {
        if (!cancelled && res?.data) setOperatorUserFetched(res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mode, currentUser?.id, currentUser?.machines_data, currentUser?.machines, operatorUserFetched]);

  // У режимі оператора: періодично оновлюємо привʼязку станків (але не чіпаємо весь застосунок)
  useEffect(() => {
    if (mode !== 'operator' || !currentUser?.id) return undefined;
    let cancelled = false;
    let inFlight = false;

    const getMachineIds = (u) => {
      const data = u?.machines_data || u?.machines || [];
      if (!Array.isArray(data)) return [];
      return data
        .map((m) => (typeof m === 'object' && m != null && 'id' in m ? m.id : m))
        .filter((id) => id != null && id !== '')
        .map((id) => Number(id))
        .sort((a, b) => a - b);
    };

    const refresh = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const res = await staffService.getById(currentUser.id);
        const fresh = res?.data;
        if (!fresh || cancelled) return;
        if (fresh.is_active === false) return;

        const prevIds = getMachineIds(operatorUserFetched || currentUser);
        const nextIds = getMachineIds(fresh);
        const changed = !operatorUserFetched || prevIds.length !== nextIds.length || prevIds.some((x, i) => x !== nextIds[i]);
        if (!changed) return;

        setOperatorUserFetched(fresh);
        // Оновимо локальний кеш currentUser для режиму оператора
        try {
          const raw = localStorage.getItem('currentUser');
          const cur = raw ? JSON.parse(raw) : null;
          if (cur && Number(cur.id) === Number(currentUser.id)) {
            const merged = {
              ...cur,
              ...fresh,
              effective_permissions: cur.effective_permissions || {},
            };
            localStorage.setItem('currentUser', JSON.stringify(merged));
          }
        } catch {
          // ignore
        }
      } catch {
        // ignore
      } finally {
        inFlight = false;
      }
    };

    refresh(); // одразу при монтуванні
    const t = setInterval(refresh, 15000);
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [mode, currentUser?.id, operatorUserFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Співробітники, яким можна призначати станки (усі активні співробітники) */
  const operatorStaffList = useMemo(() => {
    const staff = staffList || [];
    return staff.filter((s) => s.is_active !== false);
  }, [staffList]);

  const loadAll = async () => {
    setLoading(true);
    try {
      await loadOrders();
      const promises = [
        machineService.getAllForSelect(),
        machineBreakService.getAll(),
        maintenancePlanService.getAll(),
        productService.getAll(),
        pressFormService.getAll(),
        rawMaterialService.getAll(),
        dyeService.getAll(),
        stockItemService.getAll(),
        customerOrderService.getAll(),
      ];
      if (mode === 'planner') {
        promises.push(staffService.getAll({ page_size: 1000 }), roleService.getAll({ page_size: 500 }));
      }
      const results = await Promise.all(promises);
      const [macRes, breaksRes, maintRes, prodRes, pfRes, rmRes, dyesRes, stockRes, customerOrdersRes, staffRes, rolesRes] = results;
      setMachines(macRes.data?.results || macRes.data || []);
      setMachineBreaks(breaksRes.data?.results || breaksRes.data || []);
      setMaintenancePlans(maintRes.data?.results || maintRes.data || []);
      setProducts(prodRes.data?.results || prodRes.data || []);
      setPressForms(pfRes.data?.results || pfRes.data || []);
      setRawMaterials(rmRes.data?.results || rmRes.data || []);
      setDyes(dyesRes.data?.results || dyesRes.data || []);
      const stockItems = stockRes.data?.results || stockRes.data || [];
      setStockItemsAll(stockItems || []);
      setPackagingStock(
        (stockItems || []).filter((i) => i.item_category === 'packaging')
      );
      setCustomerOrders(customerOrdersRes.data?.results || customerOrdersRes.data || []);
      if (staffRes) {
        setStaffList(staffRes.data?.results || staffRes.data || []);
      }
      if (rolesRes) {
        setRolesList(rolesRes.data?.results || rolesRes.data || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingQc = async () => {
    if (operatorMachineIds.length === 0 || !currentUser?.id) {
      setPendingQcChecks([]);
      setCurrentQcCheck(null);
      return [];
    }
    try {
      const res = await qualityCheckService.getPending({
        machines: operatorMachineIds.join(','),
        operator_id: currentUser.id,
        auto: 1,
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setPendingQcChecks(list);
      if (list.length > 0) {
        setCurrentQcCheck(list[0]);
        setQcCompleteComment('');
        setQcCompletePassed(true);
      } else {
        setCurrentQcCheck(null);
      }
      return list;
    } catch (e) {
      console.error(e);
      return [];
    }
  };

  const loadDryingTasks = useCallback(async () => {
    if (mode !== 'operator' || operatorMachineIds.length === 0) return;
    setDryingLoading(true);
    try {
      const params = { machines: operatorMachineIds.join(',') };
      if (dryingActiveOnly) params.active_only = '1';
      const res = await orderMaterialDryingService.list(params);
      const raw = res.data?.results ?? res.data;
      setDryingRows(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.error(e);
      setDryingRows([]);
    } finally {
      setDryingLoading(false);
    }
  }, [mode, operatorMachineIds, dryingActiveOnly]);

  useEffect(() => {
    if (!isDryingView) return;
    if (operatorMachineIds.length === 0) return;
    loadDryingTasks();
  }, [isDryingView, operatorMachineIds, dryingActiveOnly, loadDryingTasks]);

  useEffect(() => {
    const id = orderQueryParam && /^\d+$/.test(orderQueryParam) ? orderQueryParam : '';
    setFilters((f) => (f.order_id === id ? f : { ...f, order_id: id }));
  }, [orderQueryParam]);

  useEffect(() => {
    loadAll();
  }, [filters.status, filters.machine, filters.product_name, filters.planned_start_after, filters.planned_start_before, filters.order_id]);

  useEffect(() => {
    if (mode === 'operator' && !isDryingView && operatorMachineIds.length > 0 && !loading) {
      loadPendingQc();
    }
  }, [mode, isDryingView, operatorMachineIds.length, loading]);

  // Лічильник непризначених замовлень (планер)
  useEffect(() => {
    if (mode !== 'planner') return;
    const refresh = () => {
      productionOrderService.unassignedOperatorCount()
        .then((r) => setUnassignedCount(r.data?.count || 0))
        .catch(() => {});
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [mode]);

  // Поллінг призначених замовлень для оператора (popup)
  // Локальний кеш оброблених замовлень щоб не показувати повторно після перезавантаження
  const respondedOrderIdsRef = useRef(new Set(
    (() => { try { return JSON.parse(localStorage.getItem('op_responded_orders') || '[]'); } catch { return []; } })()
  ));
  const markOrderResponded = (orderId) => {
    respondedOrderIdsRef.current.add(orderId);
    try { localStorage.setItem('op_responded_orders', JSON.stringify([...respondedOrderIdsRef.current])); } catch {}
  };

  const shownOrderIdRef = useRef(null);

  useEffect(() => {
    if (mode !== 'operator' || !currentUser?.id) return;
    const checkAssigned = () => {
      productionOrderService.getAll({ assigned_operator: currentUser.id, operator_response: 'pending', page_size: 50 })
        .then((r) => {
          const results = r.data?.results || r.data || [];
          const opMachineIds = new Set(
            (operatorUserFetched || currentUser)?.machines_data?.map((m) => (typeof m === 'object' ? m.id : m)) ||
            (operatorUserFetched || currentUser)?.machines || []
          );
          // Всі незакриті призначення на правильні машини
          const fresh = results.filter(
            (o) => !respondedOrderIdsRef.current.has(o.id) && (opMachineIds.size === 0 || opMachineIds.has(o.machine))
          );
          if (fresh.length > 0) {
            // Показуємо перше непоказане або оновлюємо список
            const firstId = fresh[0].id;
            if (shownOrderIdRef.current !== firstId) {
              shownOrderIdRef.current = firstId;
              setAssignedOrderPopup(fresh[0]);
              setDeclineMode(false);
              setDeclineReasonInput('');
            }
            setPendingAssignments(fresh);
          } else {
            shownOrderIdRef.current = null;
            setAssignedOrderPopup(null);
            setPendingAssignments([]);
          }
        })
        .catch(() => {});
    };
    checkAssigned();
    const id = setInterval(checkAssigned, 15000);
    return () => clearInterval(id);
  }, [mode, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Перемол на складі для обраного виробу (планер). */
  useEffect(() => {
    if (!planOrderOpen || !planForm.product) {
      setPermolStockList([]);
      return;
    }
    let cancelled = false;
    productService
      .getPermolStock(planForm.product)
      .then((res) => {
        if (!cancelled) setPermolStockList(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setPermolStockList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planOrderOpen, planForm.product]);

  /** Реальна вага з останньої наладки для обраної конфігурації. */
  useEffect(() => {
    if (!planOrderOpen || !planForm.config_id) {
      setHistoryWeightKg(null);
      setHistoryWeightMeta(null);
      return;
    }
    let cancelled = false;
    setupParameterHistoryService
      .latest({ manufacturing_config: planForm.config_id })
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        if (d?.found && d.actual_product_weight_kg != null && Number(d.actual_product_weight_kg) > 0) {
          setHistoryWeightKg(Number(d.actual_product_weight_kg));
          setHistoryWeightMeta(d);
        } else {
          setHistoryWeightKg(null);
          setHistoryWeightMeta(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryWeightKg(null);
          setHistoryWeightMeta(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [planOrderOpen, planForm.config_id]);

  useEffect(() => {
    if (!planOrderOpen || !planForm.product) {
      setAvailableBatches([]);
      return;
    }
    let cancelled = false;
    mixingBatchStockService.getAll({ status: 'available', product: planForm.product })
      .then((res) => {
        if (!cancelled) setAvailableBatches(Array.isArray(res.data) ? res.data : (res.data?.results || []));
      })
      .catch(() => { if (!cancelled) setAvailableBatches([]); });
    return () => { cancelled = true; };
  }, [planOrderOpen, planForm.product]);

  /** Матеріали замовлення: дієва сировина/барвник зменшуються на частку перемолу за техкартою; + рядок перемолу. */
  useEffect(() => {
    if (!planOrderOpen) return;
    setPlanForm((f) => {
      if (!f.product || !f.quantity) {
        return f.materials?.length ? { ...f, materials: [] } : f;
      }
      const product = products.find((p) => p.id === Number(f.product));
      if (!product) return f;

      const qty = Number(f.quantity);
      const techWeight = Number(product.product_weight) || 0;
      const unitWeight = (historyWeightKg != null && historyWeightKg > 0) ? historyWeightKg : techWeight;
      const totalKg = unitWeight * qty;
      if (totalKg <= 0) {
        return f.materials?.length ? { ...f, materials: [] } : f;
      }

      const selectedSlot = (availability || []).find((a) => a.config_id === f.config_id);
      const selectedPF = selectedSlot ? pressForms.find((pf) => pf.id === selectedSlot.press_form_id) : null;
      const permolAllowed = !selectedPF || selectedPF.allow_permol !== false;

      const permol = f.permol || defaultPlanPermol();
      const rmId = permol.raw_material_id ? Number(permol.raw_material_id) : null;
      const stockRow = rmId
        ? permolStockList.find((s) => Number(s.raw_material_id) === rmId)
        : null;
      const maxAvail = stockRow != null ? Number(stockRow.available) : 0;
      let P = 0;
      if (permolAllowed) {
        P = parsePermolKgInput(permol, totalKg);
        if (rmId && stockRow) {
          P = Math.min(P, maxAvail, totalKg);
        } else if (rmId && !stockRow) {
          P = 0;
        } else {
          P = 0;
        }
      }

      const prevMat = f.materials || [];
      const dryingByKey = new Map();
      const materialOverrideByComponent = new Map();
      prevMat.forEach((row) => {
        if (row.is_permolog) return;
        const matKey = row.raw_material ? `r${row.raw_material}` : row.dye ? `d${row.dye}` : '';
        if (matKey) dryingByKey.set(matKey, { needs_drying: Boolean(row.needs_drying), drying_time_minutes: row.drying_time_minutes ?? '' });
        if (row.component_id) {
          materialOverrideByComponent.set(`c${row.component_id}`, {
            raw_material: row.raw_material,
            raw_material_name: row.raw_material_name,
            dye: row.dye,
            dye_name: row.dye_name,
          });
        }
      });

      const newRows = [];

      (product.raw_material_components || []).forEach((c) => {
        const primaryRm = c.raw_material?.id ?? c.raw_material;
        const primaryName = c.raw_material_name || c.raw_material?.name || '';
        const pct = Number(c.percentage) || 0;
        if (!primaryRm || pct <= 0) return;
        const baseQty = (pct / 100) * totalKg;
        const virginQty = Math.max(0, baseQty - P * (pct / 100));
        if (virginQty < 0.0005) return;
        const override = materialOverrideByComponent.get(`c${c.id}`);
        const rm = override?.raw_material ?? primaryRm;
        const rmName = override?.raw_material_name ?? primaryName;
        const dryKey = `r${rm}`;
        const d = dryingByKey.get(dryKey) || dryingByKey.get(`r${primaryRm}`) || {};
        const altOptions = [
          { id: primaryRm, name: primaryName, article: null },
          ...(c.alternatives || []),
        ];
        newRows.push({
          type: 'raw_material',
          raw_material: rm,
          raw_material_name: rmName,
          dye: null,
          dye_name: '',
          quantity_required: virginQty,
          needs_drying: d.needs_drying ?? false,
          drying_time_minutes: d.drying_time_minutes ?? '',
          is_permolog: false,
          component_id: c.id,
          alternatives_options: altOptions,
        });
      });

      (product.dye_components || []).forEach((c) => {
        const primaryDy = c.dye?.id ?? c.dye;
        const primaryName = c.dye_name || c.dye?.name || '';
        const pct = Number(c.percentage) || 0;
        if (!primaryDy || pct <= 0) return;
        const baseQty = (pct / 100) * totalKg;
        const virginQty = Math.max(0, baseQty - P * (pct / 100));
        if (virginQty < 0.0005) return;
        const override = materialOverrideByComponent.get(`c${c.id}`);
        const dy = override?.dye ?? primaryDy;
        const dyName = override?.dye_name ?? primaryName;
        const dryKey = `d${dy}`;
        const d = dryingByKey.get(dryKey) || dryingByKey.get(`d${primaryDy}`) || {};
        const altOptions = [
          { id: primaryDy, name: primaryName, color_code: null },
          ...(c.alternatives || []),
        ];
        newRows.push({
          type: 'dye',
          raw_material: null,
          raw_material_name: '',
          dye: dy,
          dye_name: dyName,
          quantity_required: virginQty,
          needs_drying: d.needs_drying ?? false,
          drying_time_minutes: d.drying_time_minutes ?? '',
          is_permolog: false,
          component_id: c.id,
          alternatives_options: altOptions,
        });
      });

      // Додаткові матеріали для побічних виробів (інші групи гнізд пресформи)
      if (selectedPF && (selectedPF.cavity_groups || []).length > 0) {
        const groups = selectedPF.cavity_groups;
        const mainGroup = groups.find((g) => Number(g.product) === Number(f.product));
        if (mainGroup && mainGroup.number_of_cavities > 0) {
          const cycles = Math.ceil(qty / mainGroup.number_of_cavities);
          groups.forEach((g) => {
            if (Number(g.product) === Number(f.product)) return;
            const byProduct = products.find((p) => p.id === Number(g.product));
            if (!byProduct) return;
            const byQty = cycles * g.number_of_cavities;
            const byKg = (Number(byProduct.product_weight) || 0) * byQty;
            if (byKg <= 0) return;
            (byProduct.raw_material_components || []).forEach((c) => {
              const rm = c.raw_material?.id ?? c.raw_material;
              const pct = Number(c.percentage) || 0;
              if (!rm || pct <= 0) return;
              const addQty = (pct / 100) * byKg;
              if (addQty < 0.0005) return;
              const idx = newRows.findIndex((r) => r.raw_material === rm && !r.is_permolog);
              if (idx >= 0) {
                newRows[idx] = { ...newRows[idx], quantity_required: newRows[idx].quantity_required + addQty };
              } else {
                newRows.push({
                  type: 'raw_material',
                  raw_material: rm,
                  raw_material_name: c.raw_material_name || c.raw_material?.name || '',
                  dye: null, dye_name: '',
                  quantity_required: addQty,
                  needs_drying: false, drying_time_minutes: '',
                  is_permolog: false, component_id: null,
                });
              }
            });
            (byProduct.dye_components || []).forEach((c) => {
              const dy = c.dye?.id ?? c.dye;
              const pct = Number(c.percentage) || 0;
              if (!dy || pct <= 0) return;
              const addQty = (pct / 100) * byKg;
              if (addQty < 0.0005) return;
              const idx = newRows.findIndex((r) => r.dye === dy && !r.is_permolog);
              if (idx >= 0) {
                newRows[idx] = { ...newRows[idx], quantity_required: newRows[idx].quantity_required + addQty };
              } else {
                newRows.push({
                  type: 'dye',
                  raw_material: null, raw_material_name: '',
                  dye: dy,
                  dye_name: c.dye_name || c.dye?.name || '',
                  quantity_required: addQty,
                  needs_drying: false, drying_time_minutes: '',
                  is_permolog: false, component_id: null,
                });
              }
            });
          });
        }
      }

      if (P >= 0.01 && rmId && stockRow) {
        newRows.push({
          type: 'raw_material',
          raw_material: rmId,
          raw_material_name: stockRow.name || 'Перемол',
          article: stockRow.article,
          dye: null,
          dye_name: '',
          quantity_required: P,
          needs_drying: false,
          drying_time_minutes: '',
          is_permolog: true,
        });
      }

      return { ...f, materials: newRows };
    });
  }, [
    planOrderOpen,
    planForm.product,
    planForm.quantity,
    planForm.config_id,
    planForm.permol?.raw_material_id,
    planForm.permol?.quantity_kg,
    planForm.permol?.mode,
    planForm.permol?.pct,
    products,
    permolStockList,
    availability,
    pressForms,
    historyWeightKg,
  ]);

  useEffect(() => {
    if (!planOrderOpen || !planForm.product) {
      return;
    }
    const product = products.find((p) => p.id === Number(planForm.product));
    if (!product) return;
    const orderQty = Number(planForm.quantity) || 0;
    const prodPackComps = product.packaging_components || [];
    if (prodPackComps.length === 0) {
      setPlanForm((f) => (f.packaging_rows?.length ? { ...f, packaging_rows: [] } : f));
      return;
    }
    const newRows = prodPackComps.flatMap((comp) => {
      const matchingStock = packagingStock.filter(
        (s) => Number(s.packaging_material) === Number(comp.packaging_material)
      );
      if (matchingStock.length === 0) {
        return [{
          stock_item: '',
          no_stock: true,
          name: comp.packaging_material_name || '',
          unit: '',
          quantity: '',
          units_to_pack: orderQty,
          pack_at_machine: true,
        }];
      }
      return matchingStock.map((stockItem) => {
        const unitsPerPack = Number(comp.quantity) || 1;
        const autoQty = orderQty > 0 && unitsPerPack > 0 ? Math.ceil(orderQty / unitsPerPack) : '';
        return {
          stock_item: stockItem.id,
          name: stockItem.packaging_material_name || '',
          unit: stockItem.unit || '',
          quantity: String(autoQty),
          units_to_pack: orderQty,
          pack_at_machine: true,
        };
      });
    });
    setPlanForm((f) => ({ ...f, packaging_rows: newRows }));
  }, [planOrderOpen, planForm.product, planForm.quantity, products, packagingStock]);

  const [ganttPeriodDays, setGanttPeriodDays] = useState(7);
  const [ganttHeight, setGanttHeight] = useState(GANTT_DEFAULT_HEIGHT);

  const ganttRange = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const dayMs = 24 * 60 * 60 * 1000;
    if (ganttPeriodDays <= 1) {
      return [todayMs, todayMs + dayMs];
    }
    const half = Math.floor((ganttPeriodDays - 1) / 2);
    const startMs = todayMs - half * dayMs;
    const endMs = todayMs + (ganttPeriodDays - 1 - half) * dayMs;
    return [startMs, endMs];
  }, [ganttPeriodDays]);

  const handleOpenPlanOrder = () => {
    setEditingOrderId(null);
    setPlanOrderTab(0);
    setPlanForm({
      customer_order: '',
      customer_order_line: '',
      product: '',
      quantity: '',
      config_id: '',
      planned_start: '',
      comment: '',
      materials: [],
      packaging_rows: [],
      permol: defaultPlanPermol(),
      destination_warehouse: 'Склад готової продукції',
    });
    setAvailability([]);
    setPermolStockList([]);
    setPlanOrderOpen(true);
  };

  // Auto-open plan dialog when navigated from PlannerV2 with a pre-filled CO line
  useEffect(() => {
    const state = location.state;
    if (!state?.openPlan || !state?.coLine) return;
    const { coLine } = state;
    setEditingOrderId(null);
    setPlanOrderTab(1);
    setPlanForm((f) => ({
      ...f,
      customer_order: coLine.co_id || '',
      customer_order_line: coLine.id || '',
      product: String(coLine.product || ''),
      quantity: String(coLine.quantity || ''),
      config_id: '',
      planned_start: '',
      comment: '',
      materials: [],
      packaging_rows: [],
      destination_warehouse: 'Склад готової продукції',
    }));
    setPermolStockList([]);
    setPlanOrderOpen(true);
    window.history.replaceState({}, '');
    // trigger availability load for the product
    if (coLine.product) {
      productService.getAvailability(String(coLine.product), new Date().toISOString())
        .then((res) => {
          const slots = res.data?.results || res.data || [];
          setAvailability(slots);
          const first = slots[0];
          if (first) {
            setPlanForm((f) => ({
              ...f,
              config_id: first.config_id,
              planned_start: toIso(first.free_from) || f.planned_start,
            }));
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditOrder = async (order) => {
    setEditingOrderId(order.id);
    setPlanOrderTab(1);
    // Завантажуємо доступність для виробу щоб заповнити список пар
    let avail = [];
    try {
      const res = await productService.getAvailability(order.product, new Date().toISOString());
      avail = res.data?.results || res.data || [];
    } catch (e) {
      console.error(e);
    }
    setAvailability(avail);
    // config_id з замовлення або перший відповідний слот
    const configId = order.manufacturing_config ||
      avail.find((s) => s.press_form_id === order.press_form && s.machine_id === order.machine)?.config_id || '';
    setPlanForm({
      customer_order: order.customer_order || '',
      customer_order_line: order.customer_order_line || '',
      product: String(order.product),
      quantity: String(order.quantity),
      config_id: configId,
      planned_start: order.planned_start ? order.planned_start.slice(0, 16) : '',
      comment: order.comment || '',
      materials: (order.order_materials || []).map((m) => ({
        type: m.raw_material ? 'raw_material' : 'dye',
        raw_material: m.raw_material || null,
        raw_material_name: m.raw_material_name || '',
        dye: m.dye || null,
        dye_name: m.dye_name || '',
        quantity_required: String(m.quantity_required || ''),
        needs_drying: false,
        drying_time_minutes: '',
        is_permolog: Boolean(m.is_permolog),
      })),
      packaging_rows: (order.packaging_rows || []).map((r) => ({
        stock_item: r.stock_item != null && r.stock_item !== '' ? Number(r.stock_item) : null,
        packaging_material: r.packaging_material != null ? Number(r.packaging_material) : null,
        _pm_id: r.packaging_material != null ? Number(r.packaging_material) : null,
        quantity: String(r.quantity || ''),
        units_to_pack: '',
        pack_at_machine: r.pack_at_machine !== false,
        name: r.name || '',
        unit: r.unit || '',
      })),
      permol: defaultPlanPermol(),
      destination_warehouse: order.destination_warehouse || 'Склад готової продукції',
      needs_drying: Boolean(order.needs_drying),
      drying_time_minutes: order.drying_time_minutes != null ? String(order.drying_time_minutes) : '',
    });
    setPermolStockList([]);
    setPlanOrderOpen(true);
  };

  const handleProductChange = async (productId) => {
    setPlanForm((f) => ({
      ...f,
      product: productId,
      config_id: '',
      planned_start: '',
      permol: defaultPlanPermol(),
    }));
    if (!productId) {
      setAvailability([]);
      return;
    }
    try {
      const fromIso = new Date().toISOString();
      const res = await productService.getAvailability(productId, fromIso);
      const slots = res.data?.results || res.data || [];
      setAvailability(slots);
      const first = slots[0];
      if (first) {
        setPlanForm((f) => ({
          ...f,
          product: productId,
          config_id: first.config_id,
          planned_start: toIso(first.free_from) || f.planned_start,
          assigned_operator: '',
        }));
        // Завантажуємо операторів для першого авто-вибраного ТПА
        if (first.machine_id) {
          setMachineOperators([]);
          setMachineOperatorsLoading(true);
          staffService.getAll({ machine: first.machine_id, page_size: 200 })
            .then((r) => { setMachineOperators(r.data?.results || r.data || []); })
            .catch(() => {})
            .finally(() => setMachineOperatorsLoading(false));
        }
      }
    } catch (e) {
      console.error(e);
      setAvailability([]);
    }
  };

  const handleSlotChange = (configId) => {
    const slot = availability.find((a) => a.config_id === configId);
    setPlanForm((f) => ({
      ...f,
      config_id: configId,
      planned_start: slot ? toIso(slot.free_from) : f.planned_start,
      assigned_operator: '',
    }));
    // Завантажуємо операторів для вибраного ТПА
    if (slot?.machine_id) {
      setMachineOperators([]);
      setMachineOperatorsLoading(true);
      const loadOps = (attempt) =>
        staffService.getAll({ machine: slot.machine_id, page_size: 200 })
          .then((r) => { setMachineOperators(r.data?.results || r.data || []); })
          .catch(() => { if (attempt < 2) return loadOps(attempt + 1); })
          .finally(() => setMachineOperatorsLoading(false));
      loadOps(0);
    } else {
      setMachineOperators([]);
    }
  };

  const customerOrdersForPlanner = useMemo(() => {
    const rows = [];
    for (const o of customerOrders || []) {
      for (const ln of o.lines || []) {
        if (!ln.production_order) {
          rows.push({ ...ln, order: o, plannerKey: `${o.id}-${ln.id}` });
        }
      }
    }
    return rows;
  }, [customerOrders]);

  const customerOrderLineOptions = useMemo(() => {
    const out = [];
    for (const o of customerOrders || []) {
      for (const ln of o.lines || []) {
        out.push({
          lineId: ln.id,
          orderId: o.id,
          label: `#${o.number} — ${ln.product_name || ln.product_article || 'виріб'} × ${ln.quantity ?? ''}`,
          product: ln.product,
          quantity: ln.quantity,
          order: o,
        });
      }
    }
    return out;
  }, [customerOrders]);

  const handleTakeCustomerOrder = async (row) => {
    const co = row.order;
    if (!co || !row) return;
    setPlanForm((f) => ({
      ...f,
      customer_order: co.id,
      customer_order_line: row.id,
      product: row.product || '',
      quantity: row.quantity != null ? String(row.quantity) : '',
      comment: co.comment || '',
      permol: defaultPlanPermol(),
      destination_warehouse:
        (co.warehouse && String(co.warehouse).trim()) || 'Склад готової продукції',
    }));
    setPlanOrderTab(1);
    if (row.product) {
      await handleProductChange(row.product);
    }
  };

  const handleSubmitPlanOrder = async () => {
    const slot = availability.find((a) => a.config_id === planForm.config_id);
    if (!planForm.product) {
      alert('Оберіть виріб.');
      return;
    }
    if (!planForm.quantity || planForm.quantity < 1) {
      alert('Вкажіть кількість.');
      return;
    }
    if (!slot || !planForm.config_id) {
      alert('Оберіть пару «Станок + Прес-форма» зі списку вище (після вибору виробу зʼявляються доступні пари з часом звільнення).');
      return;
    }
    if (!planForm.planned_start) {
      alert('Вкажіть дату та час початку (план).');
      return;
    }
    const destWh = String(planForm.destination_warehouse || '').trim();
    if (!destWh) {
      alert('Оберіть склад надходження готової продукції (обовʼязково).');
      return;
    }
    const altMaterialRows = (planForm.materials || [])
      .filter((m) => !m.is_permolog)
      .flatMap((m) => {
        const alts = (m.alternatives_options || []).slice(1);
        return alts.map((opt) => {
          const altKey = `${m.component_id}_${opt.id}`;
          const altState = planAltMaterials[altKey] || {};
          const qty = Number(altState.qty || altState || 0);
          if (qty <= 0) return null;
          return {
            raw_material: m.type === 'raw_material' ? opt.id : undefined,
            dye: m.type === 'dye' ? opt.id : undefined,
            quantity_required: qty,
            needs_drying: false,
            drying_time_minutes: null,
          };
        }).filter(Boolean);
      });
    const materialsPayload = [
      ...(planForm.materials || [])
        .filter((m) => (m.raw_material || m.dye) && Number(m.quantity_required) > 0)
        .map((m) => ({
          raw_material: m.raw_material || undefined,
          dye: m.dye || undefined,
          quantity_required: Number(m.quantity_required),
          needs_drying: false,
          drying_time_minutes: null,
        })),
      ...altMaterialRows,
    ];

    if (materialsPayload.length === 0) {
      alert(
        'Немає матеріалів для резерву на складі. Перевірте техкарту та вагу виробу, або зменште частку перемолу.',
      );
      return;
    }
    if (planForm.needs_drying && (!planForm.drying_time_minutes || Number(planForm.drying_time_minutes) <= 0)) {
      alert('Вкажіть час сушіння замісу (хв).');
      return;
    }

    const packagingNote =
      (planForm.packaging_rows || [])
        .filter((p) => p.stock_item && Number(p.quantity) > 0)
        .map((p) => {
          const item = packagingStock.find((s) => Number(s.id) === Number(p.stock_item));
          const name =
            item?.packaging_material_name ||
            p.name ||
            'Упаковка';
          const unit = item?.unit || '';
          const qty = Number(p.quantity);
          const mode = p.pack_at_machine === false ? '→ пакування' : 'ТПА';
          return `${mode}: ${name}: ${qty}${unit ? ` ${unit}` : ''}`;
        })
        .join('; ') || '';
    const needsPackaging = (planForm.packaging_rows || []).some(
      (p) => p.stock_item && Number(p.quantity) > 0 && p.pack_at_machine === false,
    );
    const finalDestWh = needsPackaging ? 'Склад для пакування' : destWh;
    const payload = {
      product: Number(planForm.product),
      quantity: Number(planForm.quantity),
      press_form: slot.press_form_id,
      machine: slot.machine_id,
      planned_start: planForm.planned_start.length === 16 ? `${planForm.planned_start}:00` : planForm.planned_start,
      comment: planForm.comment || '',
      packaging_note: packagingNote,
      packaging_rows: (planForm.packaging_rows || [])
        .filter((p) => p.stock_item && Number(p.quantity) > 0)
        .map((p) => {
          const item = packagingStock.find((s) => Number(s.id) === Number(p.stock_item));
          const pm = Number(p.packaging_material || p._pm_id || item?.packaging_material) || null;
          return {
            stock_item: Number(p.stock_item),
            packaging_material: pm,
            name: item?.packaging_material_name || p.name || '',
            unit: item?.unit || p.unit || 'шт',
            quantity: Number(p.quantity),
            pack_at_machine: p.pack_at_machine !== false,
          };
        }),
      destination_warehouse: finalDestWh,
      materials: materialsPayload,
      needs_drying: Boolean(planForm.needs_drying),
      drying_time_minutes: planForm.needs_drying ? Number(planForm.drying_time_minutes) : null,
      customer_order: planForm.customer_order || null,
      customer_order_line: planForm.customer_order_line || null,
    };
    try {
      if (editingOrderId) {
        await productionOrderService.update(editingOrderId, payload);
        if (planForm.assigned_operator) {
          await productionOrderService.assignOperator(editingOrderId, planForm.assigned_operator).catch(() => {});
        }
        if (planForm.mixing_batch_stock_id) {
          await mixingBatchStockService.update(planForm.mixing_batch_stock_id, {
            status: 'used', used_in_order: editingOrderId,
          }).catch(() => {});
        }
      } else {
        const created = await productionOrderService.create(payload);
        if (planForm.assigned_operator && created.data?.id) {
          await productionOrderService.assignOperator(created.data.id, planForm.assigned_operator).catch(() => {});
        }
        if (planForm.mixing_batch_stock_id && created.data?.id) {
          await mixingBatchStockService.update(planForm.mixing_batch_stock_id, {
            status: 'used', used_in_order: created.data.id,
          }).catch(() => {});
        }
      }
      setPlanOrderOpen(false);
      await loadOrders();
      setUnassignedCount((c) => (planForm.assigned_operator ? Math.max(0, c - 1) : c + 1));
      if (planForm.customer_order) {
        try {
          const coRes = await customerOrderService.getAll();
          setCustomerOrders(coRes.data?.results || coRes.data || []);
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      const data = e.response?.data;
      let msg = '';
      const materialsError = Array.isArray(data?.materials)
        ? data.materials[0]
        : data?.materials;
      if (typeof materialsError === 'string' && materialsError.startsWith('Недостатньо на складі:')) {
        msg =
          materialsError +
          '\n\nНа складі не вистачає сировини або барвника для цього замовлення.\n' +
          'Перевірте залишки у розділі «Склад» і, за потреби, створіть запит у відділ закупівель на поповнення цієї позиції.';
      } else if (typeof materialsError === 'string') {
        msg = materialsError;
      } else if (data?.destination_warehouse) {
        const dw = data.destination_warehouse;
        msg = typeof dw === 'string' ? dw : Array.isArray(dw) ? dw[0] : JSON.stringify(dw);
      } else if (typeof data?.detail === 'string') {
        msg = data.detail;
      } else {
        msg = e.message || 'Сталася помилка при збереженні замовлення.';
      }
      setErrorDialog({ open: true, message: msg });
    }
  };

  const handleOpenBreak = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    setBreakForm({
      machine: '',
      start_at: toIso(now),
      end_at: toIso(end),
      reason: '',
    });
    setAddBreakOpen(true);
  };

  const handleSubmitBreak = async () => {
    if (!breakForm.machine || !breakForm.start_at || !breakForm.end_at || !breakForm.reason.trim()) {
      alert('Заповніть станок, дату з, дату по та причину.');
      return;
    }
    try {
      await machineBreakService.create({
        machine: Number(breakForm.machine),
        start_at: breakForm.start_at.length === 16 ? `${breakForm.start_at}:00` : breakForm.start_at,
        end_at: breakForm.end_at.length === 16 ? `${breakForm.end_at}:00` : breakForm.end_at,
        reason: breakForm.reason,
      });
      setAddBreakOpen(false);
      loadOrders();
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Помилка');
    }
  };

  const handleOpenReport = async () => {
    const inProgress = orders.filter((o) => o.status === 'in_progress');
    setReportLines(
      inProgress.map((o) => ({
        order: o.id,
        product_name: o.product_name,
        quantity: o.quantity,
        // Скільки вже виготовлено по цьому замовленню (з накопиченням на бекенді)
        // Використовуємо як підказку, а поле "Факт" нижче — ввід за поточний звіт.
        already_produced_fact: o.produced_fact ?? 0,
        produced_plan: '',
        produced_fact: '',
        reason: '',
        crushing_waste_kg: '0',
      }))
    );
    setReportOpen(true);
  };

  const handleSubmitReport = async () => {
    const reportDate = new Date().toISOString();
    const toSubmit = reportLines.filter((l) => l.produced_fact !== '' && l.produced_fact != null);
    for (let i = 0; i < toSubmit.length; i += 1) {
      const l = toSubmit[i];
      const cw = parseFloat(String(l.crushing_waste_kg ?? '').replace(',', '.'));
      if (Number.isNaN(cw) || cw < 0) {
        alert(`Рядок «${l.product_name}»: вкажіть коректну кількість відходів для дроблення (кг) або 0.`);
        return;
      }
    }
    const lines = toSubmit.map((l) => {
      const cw = parseFloat(String(l.crushing_waste_kg ?? '0').replace(',', '.'));
      return {
        order: l.order,
        produced_fact: Number(l.produced_fact),
        reason: l.reason || '',
        crushing_waste_kg: Number.isFinite(cw) ? cw : 0,
      };
    });
    try {
      await productionReportService.create({ report_date: reportDate, lines });
      setReportOpen(false);
    } catch (e) {
      const apiData = e.response?.data;
      const msg =
        apiData?.end_late_reason ||
        apiData?.detail ||
        apiData?.messages?.[0] ||
        (typeof apiData === 'string' ? apiData : JSON.stringify(apiData)) ||
        e.message ||
        'Помилка';
      alert(msg);
    }
  };

  const handleStart = async (order) => {
    const planStart = order.planned_start && new Date(order.planned_start);
    const now = new Date();
    if (planStart && now > planStart) {
      setSelectedOrder(order);
      setStartLateReason('');
      setStartLateOpen(true);
      return;
    }
    try {
      await productionOrderService.start(order.id);
      loadOrders();
    } catch (e) {
      const d = e.response?.data;
      if (d?.packaging_warning && d?.can_force) {
        setPackagingWarn({
          order,
          stage: 'start',
          detail: d.detail,
          items: d.packaging || [],
          startPayload: {},
        });
      } else if (d?.start_late_reason) {
        setSelectedOrder(order);
        setStartLateOpen(true);
      } else alert(d?.detail || e.message);
    }
  };

  const handleStartLateSubmit = async () => {
    if (!selectedOrder) return;
    try {
      await productionOrderService.start(selectedOrder.id, { start_late_reason: startLateReason });
      setStartLateOpen(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (e) {
      const d = e.response?.data;
      if (d?.packaging_warning && d?.can_force) {
        setStartLateOpen(false);
        setPackagingWarn({
          order: selectedOrder,
          stage: 'start',
          detail: d.detail,
          items: d.packaging || [],
          startPayload: { start_late_reason: startLateReason },
        });
        return;
      }
      const msg = d?.detail ?? (d?.start_late_reason && (Array.isArray(d.start_late_reason) ? d.start_late_reason[0] : d.start_late_reason)) ?? e.message;
      alert(msg);
    }
  };

  const handleSetup = (order) => {
    // Спочатку пробуємо взяти сировину прямо з замовлення (те, що було вибрано при плануванні)
    let wastes = [];
    if (order.order_materials?.length) {
      wastes = order.order_materials
        .filter((m) => m.raw_material)
        .map((m) => ({
          raw_material: m.raw_material,
          raw_material_name: m.raw_material_name || m.dye_name || `#${m.raw_material}`,
          quantity: 0,
        }));
    }

    // Якщо в замовленні немає матеріалів – підтягуємо з прес-форми (якщо задано)
    if (!wastes.length) {
      const pressForm = pressForms.find((pf) => pf.id === order.press_form);
      wastes = (pressForm?.raw_material_compositions || []).map((c) => ({
        raw_material: c.raw_material,
        raw_material_name: c.raw_material_name || c.name || `#${c.raw_material}`,
        quantity: 0,
      }));
    }

    setSetupWastes(wastes);
    setSelectedOrder(order);
    setSetupOpen(true);
  };

  const handleSetupSubmit = async () => {
    if (!selectedOrder) return;
    const wastes = setupWastes.filter((w) => w.quantity > 0).map((w) => ({ raw_material: w.raw_material, quantity: w.quantity }));
    try {
      await productionOrderService.setup(selectedOrder.id, { raw_material_wastes: wastes });
      setSetupOpen(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const handleLaunch = async (order) => {
    try {
      await productionOrderService.launch(order.id);
      loadOrders();
    } catch (e) {
      const d = e.response?.data;
      if (d?.can_force) {
        setPackagingWarn({
          order,
          stage: 'launch',
          detail: d.detail,
          items: d.packaging || [],
        });
      } else {
        alert(d?.detail || e.message);
      }
    }
  };

  const handleComplete = (order) => {
    const planEnd = order.planned_end && new Date(order.planned_end);
    const now = new Date();
    const isLate = !!(planEnd && now > planEnd);
    setSelectedOrder(order);
    setEndLateReason('');
    setCrushingWasteKg('0');
    setNonRecoverableWasteKg('');
    setCompleteOrderLate(isLate);
    setEndLateOpen(true);
  };

  const handleEndLateSubmit = async () => {
    if (!selectedOrder) return;
    if (completeOrderLate && !String(endLateReason || '').trim()) {
      alert('Вкажіть причину запізнення завершення');
      return;
    }
    let wasteKg = parseFloat(String(crushingWasteKg).replace(',', '.'));
    if (Number.isNaN(wasteKg) || wasteKg < 0) {
      alert('Вкажіть коректну кількість відходів для дроблення (кг). Можна 0, якщо відходів немає.');
      return;
    }
    const payload = { crushing_waste_kg: wasteKg };
    if (completeOrderLate) payload.end_late_reason = endLateReason;
    const nonRec = parseFloat(String(nonRecoverableWasteKg).replace(',', '.'));
    if (nonRecoverableWasteKg !== '' && !Number.isNaN(nonRec) && nonRec >= 0) {
      payload.non_recoverable_waste_kg = nonRec;
    }
    try {
      await productionOrderService.complete(selectedOrder.id, payload);
      setEndLateOpen(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (e) {
      const d = e.response?.data;
      if (d?.end_late_reason) {
        alert(typeof d.end_late_reason === 'string' ? d.end_late_reason : JSON.stringify(d.end_late_reason));
      } else if (d?.crushing_waste_kg) {
        alert(typeof d.crushing_waste_kg === 'string' ? d.crushing_waste_kg : JSON.stringify(d.crushing_waste_kg));
      } else {
        alert(d?.detail || e.message);
      }
    }
  };

  const handleOpenOperatorMachines = async () => {
    let staff = operatorStaffList || [];
    if (staff.length === 0) {
      try {
        const r = await staffService.getAll({ page_size: 1000 });
        staff = r.data?.results || r.data || [];
        setStaffList(staff);
      } catch {}
    }
    const initial = {};
    staff.forEach((s) => {
      const ids = (s.machines_data || s.machines || []).map((m) =>
        typeof m === 'object' && m != null ? m.id : m
      );
      initial[s.id] = ids.filter(Boolean);
    });
    setOperatorMachinesAssignments(initial);
    setOperatorMachinesOpen(true);
  };

  const handleOperatorMachinesChange = (staffId, machineIds) => {
    setOperatorMachinesAssignments((prev) => ({ ...prev, [staffId]: machineIds || [] }));
  };

  const handleOperatorMachinesSave = async () => {
    setOperatorMachinesSaving(true);
    try {
      for (const staff of operatorStaffList || []) {
        const machineIds = (operatorMachinesAssignments[staff.id] || []).map((id) => Number(id));
        await staffService.patch(staff.id, { machines: machineIds });
      }
      setOperatorMachinesOpen(false);
      loadAll();
    } catch (e) {
      const msg = e.response?.data?.detail || (e.response?.data && JSON.stringify(e.response.data)) || e.message || 'Помилка збереження';
      alert(msg);
    } finally {
      setOperatorMachinesSaving(false);
    }
  };

  // Оператори зміни (планер)
  const handleOpenShiftOps = async () => {
    try {
      const [res1, res2] = await Promise.all([
        productionOrderService.getAll({ status: 'in_progress', page_size: 200 }),
        productionOrderService.getAll({ status: 'planned', page_size: 200 }),
      ]);
      const allOrders = [
        ...(res1.data?.results || res1.data || []),
        ...(res2.data?.results || res2.data || []),
      ];
      const unassigned = allOrders.filter((o) => !o.assigned_operator);
      setShiftOpsOrders(unassigned);
      const initAssign = {};
      unassigned.forEach((o) => { initAssign[o.id] = ''; });
      setShiftOpsAssignments(initAssign);
      // Для кожного унікального machine — завантажуємо операторів
      const machineIds = [...new Set(unassigned.map((o) => o.machine))];
      const opsByMachine = {};
      await Promise.all(machineIds.map(async (mid) => {
        const r = await staffService.getAll({ machine: mid }).catch(() => ({ data: [] }));
        opsByMachine[mid] = r.data?.results || r.data || [];
      }));
      setShiftOpsOperators(opsByMachine);
    } catch {}
    setShiftOpsOpen(true);
  };

  const handleShiftOpsSave = async () => {
    setShiftOpsSaving(true);
    try {
      for (const [orderId, opId] of Object.entries(shiftOpsAssignments)) {
        if (opId) {
          await productionOrderService.assignOperator(orderId, opId);
        }
      }
      setShiftOpsOpen(false);
      await loadOrders();
      productionOrderService.unassignedOperatorCount()
        .then((r) => setUnassignedCount(r.data?.count || 0)).catch(() => {});
    } catch (e) {
      alert(e.response?.data?.detail || 'Помилка збереження');
    } finally {
      setShiftOpsSaving(false);
    }
  };

  // Звіт за зміну (оператор)
  const handleOpenShiftReport = async () => {
    if (!currentUser?.id) return;
    const opId = Number(currentUser.id);
    try {
      const res = await productionOrderService.getAll({
        assigned_operator: opId,
        operator_response: 'accepted',
        status: 'in_progress',
        page_size: 200,
      });
      const allOrders = res.data?.results || res.data || [];
      // Фільтр на фронтенді як захист від непрацюючих бекенд-фільтрів
      const myOrders = allOrders.filter(
        (o) => Number(o.assigned_operator) === opId && o.operator_response === 'accepted'
      );
      const lines = myOrders.map((o) => ({
        order: o,
        produced_qty: '',
        waste_kg: '',
        comment: '',
      }));
      setShiftReportLines(lines);
      setShiftReportStep(0);
      setShiftReportDone(false);
      setShiftReportOpen(true);
    } catch {
      alert('Помилка завантаження замовлень');
    }
  };

  const handleShiftReportSubmit = async () => {
    setShiftReportSubmitting(true);
    try {
      const payload = shiftReportLines.map((l) => ({
        order: l.order.id,
        operator: currentUser.id,
        produced_qty: Number(l.produced_qty) || 0,
        waste_kg: Number(l.waste_kg) || 0,
        comment: l.comment || '',
      }));
      await operatorShiftReportService.create(payload);
      setShiftReportDone(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Помилка збереження звіту');
    } finally {
      setShiftReportSubmitting(false);
    }
  };

  // Відповідь оператора на призначення
  const handleOperatorAccept = async () => {
    if (!assignedOrderPopup) return;
    setPopupSubmitting(true);
    try {
      await productionOrderService.operatorResponse(assignedOrderPopup.id, 'accepted');
      markOrderResponded(assignedOrderPopup.id);
      // Перейти до наступного в черзі
      const remaining = pendingAssignments.filter((o) => o.id !== assignedOrderPopup.id && !respondedOrderIdsRef.current.has(o.id));
      if (remaining.length > 0) {
        shownOrderIdRef.current = remaining[0].id;
        setAssignedOrderPopup(remaining[0]);
        setDeclineMode(false);
        setDeclineReasonInput('');
        setPendingAssignments(remaining);
      } else {
        shownOrderIdRef.current = null;
        setAssignedOrderPopup(null);
        setPendingAssignments([]);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Помилка');
    } finally {
      setPopupSubmitting(false);
    }
  };

  const handleOperatorDecline = async () => {
    if (!assignedOrderPopup) return;
    if (!declineReasonInput.trim()) {
      alert('Вкажіть причину відмови');
      return;
    }
    setPopupSubmitting(true);
    try {
      await productionOrderService.operatorResponse(assignedOrderPopup.id, 'declined', declineReasonInput.trim());
      markOrderResponded(assignedOrderPopup.id);
      const remaining = pendingAssignments.filter((o) => o.id !== assignedOrderPopup.id && !respondedOrderIdsRef.current.has(o.id));
      if (remaining.length > 0) {
        shownOrderIdRef.current = remaining[0].id;
        setAssignedOrderPopup(remaining[0]);
        setDeclineMode(false);
        setDeclineReasonInput('');
        setPendingAssignments(remaining);
      } else {
        shownOrderIdRef.current = null;
        setAssignedOrderPopup(null);
        setPendingAssignments([]);
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Помилка');
    } finally {
      setPopupSubmitting(false);
    }
  };

  const handleOpenQcTrigger = () => {
    setQcTriggerMachines([]);
    setQcTriggerOperator(currentUser?.id || '');
    setQcTriggerOpen(true);
  };

  const handleOpenQcForOperatorNow = async () => {
    if (mode !== 'operator') return;
    const list = await loadPendingQc();
    // Не використовуємо currentQcCheck / pendingQcChecks зі стану — після await вони ще старі (закриття багу React).
    if (list.length === 0) {
      setQcNoPendingOpen(true);
    }
  };

  const handleCreateQcForMyMachines = async () => {
    if (!operatorMachineIds.length || !currentUser?.id) return;
    setQcNoPendingSubmitting(true);
    try {
      await qualityCheckService.trigger({
        machines: operatorMachineIds,
        operator: currentUser.id,
        triggered_by: currentUser.id,
        source: 'operator',
      });
      setQcNoPendingOpen(false);
      const after = await loadPendingQc();
      if (after.length === 0) {
        alert(
          'Відкриті перевірки не зʼявились. Можливо, для цих ТПА вже є незавершений контроль, або зверніться до адміністратора (налаштування періоду авто-КЯ на ТПА).'
        );
      }
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Не вдалося створити перевірки');
    } finally {
      setQcNoPendingSubmitting(false);
    }
  };

  const handleQcTriggerSubmit = async () => {
    if (qcTriggerMachines.length === 0) {
      alert('Оберіть хоча б один станок');
      return;
    }
    setQcTriggerSubmitting(true);
    try {
      await qualityCheckService.trigger({
        machines: qcTriggerMachines,
        operator: qcTriggerOperator || undefined,
        triggered_by: currentUser?.id || undefined,
      });
      setQcTriggerOpen(false);
    } catch (e) {
      alert(e.response?.data?.detail || e.message || 'Помилка створення перевірок');
    } finally {
      setQcTriggerSubmitting(false);
    }
  };

  const handleQcCompleteSubmit = async () => {
    if (!currentQcCheck) return;
    const comment = (qcCompleteComment || '').trim();
    if (!comment) {
      alert('Коментар обовʼязковий');
      return;
    }
    setQcCompleteSubmitting(true);
    try {
      await qualityCheckService.complete(currentQcCheck.id, {
        passed: qcCompletePassed,
        comment,
      });
      setCurrentQcCheck(null);
      setQcCompleteComment('');
      await loadPendingQc();
    } catch (e) {
      alert(e.response?.data?.comment?.[0] || e.response?.data?.detail || e.message || 'Помилка збереження');
    } finally {
      setQcCompleteSubmitting(false);
    }
  };

  const handleDryingStart = async (row) => {
    setDryingBusyId(row.id);
    try {
      await orderMaterialDryingService.start(row.id);
      await loadDryingTasks();
      await loadOrders();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : e.message || 'Помилка');
    } finally {
      setDryingBusyId(null);
    }
  };

  const handleDryingComplete = async (row) => {
    setDryingBusyId(row.id);
    try {
      await orderMaterialDryingService.complete(row.id);
      await loadDryingTasks();
      await loadOrders();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === 'string' ? d : e.message || 'Помилка');
    } finally {
      setDryingBusyId(null);
    }
  };

  const handleDetails = async (order) => {
    setDetailsOpen(true);
    setSelectedOrder(order);
    try {
      const res = await productionOrderService.getById(order.id);
      const data = res.data;
      // Нормалізуємо відповідь, щоб уникнути циклічних посилань (лог може містити order)
      const safe = {
        ...data,
        logs: (data.logs || []).map((log) => ({
          id: log.id,
          action: log.action,
          details: log.details && typeof log.details === 'object' ? { ...log.details } : log.details,
          created_at: log.created_at,
        })),
        order_materials: (data.order_materials || []).map((m) => ({
          id: m.id,
          raw_material: m.raw_material,
          raw_material_name: m.raw_material_name,
          dye: m.dye,
          dye_name: m.dye_name,
          quantity_required: m.quantity_required,
          needs_drying: m.needs_drying,
          drying_time_minutes: m.drying_time_minutes,
          drying_status: m.drying_status,
          drying_status_display: m.drying_status_display,
          drying_started_at: m.drying_started_at,
          drying_completed_at: m.drying_completed_at,
          drying_deadline_at: m.drying_deadline_at,
        })),
        raw_material_wastes: (data.raw_material_wastes || []).map((w) => ({
          id: w.id,
          raw_material: w.raw_material,
          raw_material_name: w.raw_material_name,
          quantity: w.quantity,
        })),
      };
      setSelectedOrder(safe);
    } catch (e) {
      console.error(e);
    }
  };

  const displayedOrders =
    mode === 'operator' && operatorMachineIds.length > 0
      ? orders.filter((o) => {
          const mid = o.machine && (typeof o.machine === 'object' ? o.machine.id : o.machine);
          return mid != null && operatorMachineIds.includes(Number(mid));
        })
      : orders;

  const displayedMachines =
    mode === 'operator' && operatorMachineIds.length > 0
      ? (machines || []).filter((m) => operatorMachineIds.includes(Number(m.id)))
      : machines;

  const ordersForGantt = useMemo(
    () => displayedOrders.filter((o) => o.status !== 'cancelled'),
    [displayedOrders]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (
    isDryingView &&
    currentUser?.effective_permissions &&
    currentUser.effective_permissions['menu.production.drying'] === false
  ) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Сушіння
        </Typography>
        <Typography color="text.secondary">
          У вашій ролі або персональних налаштуваннях не надано доступ до цього розділу. Зверніться до адміністратора
          (Налаштування → Ролі або Персонал).
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {isDryingView ? (
        <>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Сушіння
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Розділ «Виробництво». Матеріали з плану замовлення, де вказано «Потрібне сушіння», — для ваших призначених
            ТПА. Дедлайн за замовчуванням збігається з плановим початком виробництва.
          </Typography>
          {operatorMachineIds.length === 0 ? (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Вам не призначено жодного станка. Зверніться до адміністратора (Налаштування → Персонал).
            </Typography>
          ) : (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                Черга сушіння
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Встигніть завершити сушіння до запуску відповідного замовлення на ТПА.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={dryingActiveOnly}
                      onChange={(e) => setDryingActiveOnly(e.target.checked)}
                    />
                  }
                  label="Без завершених"
                />
                <Button size="small" variant="outlined" onClick={() => loadDryingTasks()} disabled={dryingLoading}>
                  Оновити
                </Button>
              </Box>
              {dryingLoading && (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              )}
              {!dryingLoading && dryingRows.length === 0 && (
                <Typography color="text.secondary">
                  Немає задач сушіння для ваших станків (або ще не заплановано рядків з «Потрібне сушіння»).
                </Typography>
              )}
              {!dryingLoading && dryingRows.length > 0 && (
                <TableContainer>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Замовлення</TableCell>
                        <TableCell>Виріб</TableCell>
                        <TableCell>ТПА</TableCell>
                        <TableCell>Матеріал</TableCell>
                        <TableCell align="right">Кількість</TableCell>
                        <TableCell align="right">Час (хв)</TableCell>
                        <TableCell>Дедлайн</TableCell>
                        <TableCell align="right">Дії</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dryingRows.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            bgcolor:
                              row.is_overdue && row.drying_status !== 'completed'
                                ? 'rgba(255, 152, 0, 0.14)'
                                : undefined,
                          }}
                        >
                          <TableCell>{orderRefFromRow(row)}</TableCell>
                          <TableCell>{row.product_name}</TableCell>
                          <TableCell>{row.machine_identifier}</TableCell>
                          <TableCell>{row.material_label || row.raw_material_name || row.dye_name || '—'}</TableCell>
                          <TableCell align="right">{row.quantity_required}</TableCell>
                          <TableCell align="right">{row.drying_time_minutes ?? '—'}</TableCell>
                          <TableCell>
                            {formatDt(row.drying_deadline_at)}
                            {row.is_overdue && row.drying_status !== 'completed' && (
                              <Chip label="Прострочено" size="small" color="warning" sx={{ ml: 0.5, height: 20 }} />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              {(row.drying_status === 'pending' || row.drying_status === 'cancelled') && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleDryingStart(row)}
                                  disabled={dryingBusyId === row.id}
                                >
                                  Взято в роботу
                                </Button>
                              )}
                              {row.drying_status === 'in_progress' && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={() => handleDryingComplete(row)}
                                  disabled={dryingBusyId === row.id}
                                >
                                  Виконано
                                </Button>
                              )}
                              {row.drying_status === 'completed' && (
                                <Typography variant="body2" color="success.main">
                                  Виконано
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}
        </>
      ) : (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            {mode === 'operator' ? 'Робоче місце оператора' : 'Планер'}
          </Typography>

          {mode === 'planner' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button variant="contained" startIcon={<AddTaskIcon />} onClick={handleOpenPlanOrder}>
                Запланувати замовлення
              </Button>
              <Button variant="outlined" startIcon={<VerifiedUserIcon />} onClick={handleOpenQcTrigger}>
                Контроль якості
              </Button>
              <Button variant="outlined" startIcon={<PauseCircleIcon />} onClick={handleOpenBreak}>
                Додати перерву
              </Button>
              <Button variant="outlined" startIcon={<PeopleIcon />} onClick={handleOpenOperatorMachines}>
                Станки операторів
              </Button>
              <Badge badgeContent={unassignedCount || null} color="error">
                <Button variant="outlined" startIcon={<PeopleIcon />} onClick={handleOpenShiftOps}>
                  Оператори зміни
                </Button>
              </Badge>
            </Box>
          )}

          {mode === 'operator' && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button variant="contained" color="success" startIcon={<AssessmentIcon />} onClick={handleOpenShiftReport}>
                Подати звіт за зміну
              </Button>
              <Button variant="outlined" startIcon={<AssessmentIcon />} onClick={handleOpenReport}>
                Створити звіт
              </Button>
              <Button variant="outlined" startIcon={<VerifiedUserIcon />} onClick={handleOpenQcForOperatorNow}>
                Контроль якості
              </Button>
            </Box>
          )}

          {mode === 'operator' && operatorMachineIds.length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Вам не призначено жодного станка. Зверніться до адміністратора (Налаштування → Персонал).
            </Typography>
          )}

          {(mode !== 'operator' || operatorMachineIds.length > 0) && (
            <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography variant="subtitle1">
                Діаграма Ганта (станки та заплановані роботи)
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Період перегляду</InputLabel>
                <Select
                  value={ganttPeriodDays}
                  label="Період перегляду"
                  onChange={(e) => setGanttPeriodDays(Number(e.target.value))}
                >
                  <MenuItem value={1}>1 день</MenuItem>
                  <MenuItem value={3}>3 дні</MenuItem>
                  <MenuItem value={7}>7 днів</MenuItem>
                  <MenuItem value={30}>Місяць</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <GanttBlock
              orders={ordersForGantt}
              machineBreaks={machineBreaks}
              maintenancePlans={maintenancePlans}
              machines={displayedMachines}
              zoomDays={ganttPeriodDays}
              height={ganttHeight}
              onHeightChange={setGanttHeight}
              scrollToTodayOnMount
            />
          </Paper>

          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Замовлення
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <TextField
          select
          size="small"
          label="Статус"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Усі</MenuItem>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k}>
              {v}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Дата з"
          type="datetime-local"
          value={filters.planned_start_after}
          onChange={(e) => setFilters((f) => ({ ...f, planned_start_after: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <TextField
          size="small"
          label="Дата по"
          type="datetime-local"
          value={filters.planned_start_before}
          onChange={(e) => setFilters((f) => ({ ...f, planned_start_before: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
        <TextField
          size="small"
          label="Назва виробу"
          value={filters.product_name}
          onChange={(e) => setFilters((f) => ({ ...f, product_name: e.target.value }))}
          sx={{ minWidth: 160 }}
        />
        <TextField
          size="small"
          label="№ замовлення"
          value={filters.order_id}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '');
            setFilters((f) => ({ ...f, order_id: v }));
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (v) next.set('order', v);
                else next.delete('order');
                return next;
              },
              { replace: true }
            );
          }}
          inputProps={{ inputMode: 'numeric' }}
          sx={{ width: 130 }}
        />
        <TextField
          select
          size="small"
          label="Станок"
          value={filters.machine}
          onChange={(e) => setFilters((f) => ({ ...f, machine: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Усі</MenuItem>
          {(displayedMachines || []).map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.identifier}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Виріб</TableCell>
              <TableCell>К-сть</TableCell>
              <TableCell>Станок</TableCell>
              <TableCell>Початок (план)</TableCell>
              <TableCell>Кінець (план)</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedOrders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>{order.product_name}</TableCell>
                <TableCell>{order.quantity}</TableCell>
                <TableCell>{order.machine_identifier}</TableCell>
                <TableCell>{formatDt(order.planned_start)}</TableCell>
                <TableCell>{formatDt(order.planned_end)}</TableCell>
                <TableCell>{STATUS_LABELS[order.status] || order.status}</TableCell>
                <TableCell align="right">
                  {order.status === 'planned' && (
                    <Tooltip title="Редагувати замовлення">
                      <IconButton size="small" onClick={() => handleEditOrder(order)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {order.status === 'planned' && (
                    <Tooltip title="Почав виконання (зафіксує фактичний час початку)">
                      <IconButton size="small" onClick={() => handleStart(order)}>
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {order.status === 'in_progress' && !order.setup_at && (
                    <Tooltip title="Наладив (вказати втрати сировини на запуск)">
                      <IconButton size="small" onClick={() => handleSetup(order)}>
                        <BuildIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {order.status === 'in_progress' && order.setup_at && !order.launched_at && (
                    <Tooltip title="Запустив (перерахунок планового часу завершення)">
                      <IconButton size="small" onClick={() => handleLaunch(order)}>
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {order.status === 'in_progress' && order.launched_at && (
                    <Tooltip title="Завершив (зафіксує фактичний час завершення, списує матеріали зі складу)">
                      <IconButton size="small" onClick={() => handleComplete(order)}>
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Деталі замовлення та історія змін">
                    <IconButton size="small" onClick={() => handleDetails(order)}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
            </>
          )}

        </>
      )}

      {/* Error dialog */}
      <Dialog
        open={errorDialog.open}
        onClose={() => setErrorDialog((d) => ({ ...d, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Помилка при збереженні замовлення</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
            {errorDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialog((d) => ({ ...d, open: false }))}>
            Зрозуміло
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan order dialog */}
      <Dialog open={planOrderOpen} onClose={() => { setPlanOrderOpen(false); setEditingOrderId(null); setPlanAltMaterials({}); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>{editingOrderId ? 'Редагувати замовлення' : 'Запланувати замовлення'}</DialogTitle>
        <DialogContent>
          <Tabs
            value={planOrderTab}
            onChange={(_, v) => setPlanOrderTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="З замовлень" />
            <Tab label="Нове" />
          </Tabs>

          {planOrderTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Позиції замовлень клієнта без виробничого замовлення. Натисніть «Взяти в роботу», щоб підставити виріб і
                кількість у форму планування.
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Номер</TableCell>
                      <TableCell>Виріб</TableCell>
                      <TableCell>К-сть</TableCell>
                      <TableCell>Дата</TableCell>
                      <TableCell>Дедлайн</TableCell>
                      <TableCell align="right">Дія</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(customerOrdersForPlanner || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography variant="body2" color="text.secondary">
                            Немає доступних замовлень
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      customerOrdersForPlanner.map((row) => (
                        <TableRow key={row.plannerKey} hover>
                          <TableCell>{row.order?.number || `#${row.order?.id}`}</TableCell>
                          <TableCell>{row.product_name || '—'}</TableCell>
                          <TableCell>{row.quantity ?? '—'}</TableCell>
                          <TableCell>
                            {row.order?.order_date
                              ? new Date(row.order.order_date).toLocaleDateString('uk-UA')
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {row.order?.deadline
                              ? new Date(row.order.deadline).toLocaleDateString('uk-UA')
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="outlined" onClick={() => handleTakeCustomerOrder(row)}>
                              Взяти в роботу
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {planOrderTab === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Позиція замовлення клієнта (опціонально)"
                value={planForm.customer_order_line || ''}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : '';
                  const opt = customerOrderLineOptions.find((x) => x.lineId === val);
                  setPlanForm((f) => ({
                    ...f,
                    customer_order_line: val || '',
                    customer_order: opt ? opt.orderId : '',
                    product: opt?.product || f.product,
                    quantity: opt?.quantity != null ? String(opt.quantity) : f.quantity,
                    comment: opt?.order?.comment || f.comment,
                    destination_warehouse: opt?.order
                      ? (opt.order.warehouse && String(opt.order.warehouse).trim()) || 'Склад готової продукції'
                      : f.destination_warehouse || 'Склад готової продукції',
                  }));
                  if (opt?.product) handleProductChange(opt.product);
                }}
                fullWidth
              >
                <MenuItem value="">— без привʼязки до замовлення —</MenuItem>
                {customerOrderLineOptions.map((opt) => (
                  <MenuItem key={opt.lineId} value={opt.lineId}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Виріб"
                value={planForm.product}
                onChange={(e) => handleProductChange(e.target.value)}
                fullWidth
              >
                <MenuItem value="">— обрати —</MenuItem>
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </TextField>
            <TextField
              type="number"
              label="Кількість"
              value={planForm.quantity}
              onChange={(e) => {
                const q = e.target.value;
                setPlanForm((f) => ({ ...f, quantity: q }));
              }}
              fullWidth
              inputProps={{ min: 1 }}
            />
            {availableBatches.length > 0 && (
              <TextField
                select
                label="Залишок замісу (Б2) — опціонально"
                value={planForm.mixing_batch_stock_id || ''}
                onChange={(e) => setPlanForm((f) => ({ ...f, mixing_batch_stock_id: e.target.value }))}
                fullWidth
                helperText="Якщо є готовий заміс для цього виробу — оберіть для повторного використання"
              >
                <MenuItem value="">— не використовувати —</MenuItem>
                {availableBatches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.quantity_kg} кг — {b.note || 'без примітки'} ({new Date(b.created_at).toLocaleDateString('uk-UA')})
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              select
              required
              label="Склад надходження готової продукції"
              value={planForm.destination_warehouse || ''}
              onChange={(e) =>
                setPlanForm((f) => ({ ...f, destination_warehouse: e.target.value }))
              }
              fullWidth
              helperText="Куди надійде випуск після завершення замовлення. Можна змінити щодо замовлення клієнта."
            >
              {PLANNER_DESTINATION_WAREHOUSES.map((w) => (
                <MenuItem key={w} value={w}>
                  {w}
                </MenuItem>
              ))}
            </TextField>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Станок + Прес-форма (коли звільняться)
              </Typography>
              {!planForm.product ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  Спочатку оберіть виріб — зʼявиться список пар станок/прес-форма з часом звільнення.
                </Typography>
              ) : availability.length === 0 ? (
                <Typography variant="body2" color="warning.main" sx={{ py: 1 }}>
                  Для цього виробу немає пар станок/прес-форма. Додайте конфігурацію виготовлення у розділі Вироби (поле «Станки та прес-форми»).
                </Typography>
              ) : (
                <TextField
                  select
                  label="Оберіть пару"
                  value={planForm.config_id || ''}
                  onChange={(e) => handleSlotChange(e.target.value ? Number(e.target.value) : '')}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="">— обрати пару —</MenuItem>
                  {availability.map((slot) => (
                    <MenuItem key={slot.config_id} value={slot.config_id}>
                      {slot.machine_identifier} / {slot.press_form_identifier} — звільняться з {formatDt(slot.free_from)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Box>
            {planForm.config_id && (
              <FormControl fullWidth>
                <InputLabel>Оператор</InputLabel>
                <Select
                  label="Оператор"
                  value={planForm.assigned_operator}
                  onChange={(e) => setPlanForm((f) => ({ ...f, assigned_operator: e.target.value }))}
                >
                  <MenuItem value=""><em>— не призначено —</em></MenuItem>
                  {machineOperatorsLoading ? (
                    <MenuItem disabled><em>Завантаження...</em></MenuItem>
                  ) : (
                    machineOperators.map((op) => (
                      <MenuItem key={op.id} value={op.id}>{op.full_name}</MenuItem>
                    ))
                  )}
                </Select>
                {machineOperators.length === 0 && !machineOperatorsLoading && planForm.config_id && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    На цей ТПА не призначено жодного оператора
                  </Typography>
                )}
              </FormControl>
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                Сировина та барвники
              </Typography>
              {planForm.product && planForm.quantity && historyWeightKg != null && (
                <Typography variant="caption" color="primary.main" sx={{ display: 'block', mb: 0.5 }}>
                  Вага з наладки: {Math.round(historyWeightKg * 1000)} г/шт
                  {historyWeightMeta?.recorded_at
                    ? ` (${new Date(historyWeightMeta.recorded_at).toLocaleString('uk-UA', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })})`
                    : ''}
                  {(() => {
                    const tw = Number(products.find((p) => p.id === Number(planForm.product))?.product_weight) || 0;
                    return tw > 0 && Math.abs(historyWeightKg - tw) > 0.00005
                      ? ` · техкарта: ${Math.round(tw * 1000)} г`
                      : '';
                  })()}
                </Typography>
              )}
              {planForm.product && planForm.quantity ? (
                (() => {
                  const planProduct = products.find((p) => p.id === Number(planForm.product));
                  const techW = Number(planProduct?.product_weight) || 0;
                  const unitW = (historyWeightKg != null && historyWeightKg > 0) ? historyWeightKg : techW;
                  const planTotalKg = unitW * Number(planForm.quantity);
                  const virginMaterials = (planForm.materials || [])
                    .map((m, i) => ({ ...m, _planIdx: i }))
                    .filter((m) => !m.is_permolog);

                  if (planTotalKg <= 0) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                        У виробі не задана вага виробу (г) — неможливо порахувати сировину. Заповніть у розділі Вироби.
                      </Typography>
                    );
                  }
                  if (virginMaterials.length === 0 && !(planForm.materials || []).some((m) => m.is_permolog)) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                        У виробі не заповнена техкарта (сировина та барвники з відсотками). Заповніть у розділі Вироби.
                      </Typography>
                    );
                  }
                  if (virginMaterials.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                        Нову сировину та барвники не резервовано — усю масу замовлення ({planTotalKg.toFixed(3)} кг)
                        покрито перемолом за поточними налаштуваннями.
                      </Typography>
                    );
                  }
                  const altRows = virginMaterials.flatMap((row) => {
                    const alts = (row.alternatives_options || []).slice(1);
                    return alts.map((opt) => ({
                      component_id: row.component_id,
                      primary_name: row.raw_material_name || row.dye_name || '—',
                      type: row.type,
                      raw_material: row.type === 'raw_material' ? opt.id : null,
                      raw_material_name: row.type === 'raw_material' ? opt.name : '',
                      dye: row.type === 'dye' ? opt.id : null,
                      dye_name: row.type === 'dye' ? opt.name : '',
                      altKey: `${row.component_id}_${opt.id}`,
                    }));
                  });
                  return (
                    <>
                      <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Матеріал</TableCell>
                              <TableCell align="right">Кількість (кг)</TableCell>
                              <TableCell align="right">На складі (кг)</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {virginMaterials.map((row) => {
                              const idx = row._planIdx;
                              const keyId = row.component_id || row.raw_material || row.dye;
                              const stockItem = (stockItemsAll || []).find((si) => {
                                const rm = si.raw_material?.id ?? si.raw_material;
                                const dy = si.dye?.id ?? si.dye;
                                if (row.raw_material) return Number(rm) === Number(row.raw_material);
                                if (row.dye) return Number(dy) === Number(row.dye);
                                return false;
                              });
                              const availQty = stockItem?.quantity != null ? Number(stockItem.quantity) : null;
                              return (
                                <TableRow key={keyId}>
                                  <TableCell>
                                    {row.raw_material_name || row.dye_name || '—'}
                                  </TableCell>
                                  <TableCell align="right">
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={row.quantity_required ?? ''}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setPlanForm((f) => {
                                          const next = [...(f.materials || [])];
                                          next[idx] = { ...next[idx], quantity_required: val };
                                          return { ...f, materials: next };
                                        });
                                      }}
                                      inputProps={{ min: 0, step: 0.001 }}
                                      sx={{ width: 120 }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    {availQty == null ? '—' : availQty.toFixed(3)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      <Box sx={{ mt: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fafafa' }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={Boolean(planForm.needs_drying)}
                              onChange={(e) => setPlanForm((f) => ({
                                ...f,
                                needs_drying: e.target.checked,
                                drying_time_minutes: e.target.checked ? (f.drying_time_minutes || '') : '',
                              }))}
                            />
                          }
                          label={<Typography variant="subtitle2">Потребує сушіння (заміс)</Typography>}
                        />
                        {planForm.needs_drying && (
                          <TextField
                            size="small"
                            type="number"
                            label="Час сушіння (хв)"
                            value={planForm.drying_time_minutes ?? ''}
                            onChange={(e) => setPlanForm((f) => ({ ...f, drying_time_minutes: e.target.value }))}
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ width: 200, mt: 1, display: 'block' }}
                          />
                        )}
                      </Box>
                      {altRows.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                            Альтернативні матеріали
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Альтернатива (замість)</TableCell>
                                  <TableCell align="right">Кількість (кг)</TableCell>
                                  <TableCell align="right">На складі (кг)</TableCell>
                                  <TableCell align="center">Сушіння</TableCell>
                                  <TableCell align="right">Час (хв)</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {altRows.map((ar) => {
                                  const stockItem = (stockItemsAll || []).find((si) => {
                                    const rm = si.raw_material?.id ?? si.raw_material;
                                    const dy = si.dye?.id ?? si.dye;
                                    if (ar.raw_material) return Number(rm) === Number(ar.raw_material);
                                    if (ar.dye) return Number(dy) === Number(ar.dye);
                                    return false;
                                  });
                                  const availQty = stockItem?.quantity != null ? Number(stockItem.quantity) : null;
                                  const altState = planAltMaterials[ar.altKey] || {};
                                  const qty = typeof altState === 'object' ? (altState.qty ?? '') : (altState ?? '');
                                  const needsDrying = typeof altState === 'object' ? Boolean(altState.needs_drying) : false;
                                  const dryingMin = typeof altState === 'object' ? (altState.drying_time_minutes ?? '') : '';
                                  const setAlt = (patch) => setPlanAltMaterials((prev) => {
                                    const cur = typeof prev[ar.altKey] === 'object' ? prev[ar.altKey] : { qty: prev[ar.altKey] ?? '' };
                                    return { ...prev, [ar.altKey]: { ...cur, ...patch } };
                                  });
                                  return (
                                    <TableRow key={ar.altKey}>
                                      <TableCell>
                                        <Box>
                                          <Typography variant="body2">{ar.raw_material_name || ar.dye_name || '—'}</Typography>
                                          <Typography variant="caption" color="text.secondary">замість: {ar.primary_name}</Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={qty}
                                          onChange={(e) => setAlt({ qty: e.target.value })}
                                          inputProps={{ min: 0, step: 0.001 }}
                                          sx={{ width: 120 }}
                                          placeholder="0"
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        {availQty == null ? '—' : availQty.toFixed(3)}
                                      </TableCell>
                                      <TableCell align="center">
                                        <Checkbox
                                          checked={needsDrying}
                                          onChange={(e) => setAlt({ needs_drying: e.target.checked, drying_time_minutes: e.target.checked ? dryingMin : '' })}
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={dryingMin}
                                          disabled={!needsDrying}
                                          onChange={(e) => setAlt({ drying_time_minutes: e.target.value })}
                                          inputProps={{ min: 0, step: 1 }}
                                          sx={{ width: 100 }}
                                        />
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </>
                  );
                })()
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                  Оберіть виріб та кількість — кількість сировини та барвників підрахується автоматично за техкартою виробу.
                </Typography>
              )}
              {planForm.product && planForm.quantity && permolStockList.length > 0 && (() => {
                const selectedSlot = (availability || []).find((a) => a.config_id === planForm.config_id);
                const selectedPF = selectedSlot ? pressForms.find((pf) => pf.id === selectedSlot.press_form_id) : null;
                if (selectedPF && selectedPF.allow_permol === false) return null;
                return true;
              })() && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Перемол
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={48}> </TableCell>
                          <TableCell>Назва</TableCell>
                          <TableCell>Артикул</TableCell>
                          <TableCell align="right">Доступно (кг)</TableCell>
                          <TableCell align="right">Склад</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {permolStockList.map((ps) => {
                          const selected =
                            Number(planForm.permol?.raw_material_id) === Number(ps.raw_material_id);
                          return (
                            <TableRow
                              key={ps.raw_material_id}
                              selected={selected}
                              hover
                              onClick={() => {
                                setPlanForm((f) => ({
                                  ...f,
                                  permol: {
                                    ...(f.permol || defaultPlanPermol()),
                                    raw_material_id: ps.raw_material_id,
                                  },
                                }));
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Checkbox
                                  size="small"
                                  checked={selected}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setPlanForm((f) => ({
                                      ...f,
                                      permol: {
                                        ...(f.permol || defaultPlanPermol()),
                                        raw_material_id: e.target.checked ? ps.raw_material_id : '',
                                      },
                                    }));
                                  }}
                                />
                              </TableCell>
                              <TableCell>{ps.name}</TableCell>
                              <TableCell>{ps.article || '—'}</TableCell>
                              <TableCell align="right">
                                {Number(ps.available).toFixed(3)}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPermolDetailOpen(ps);
                                  }}
                                >
                                  Деталі
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {Number(planForm.permol?.raw_material_id) > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                      <ToggleButtonGroup
                        size="small"
                        value={planForm.permol?.mode || 'kg'}
                        exclusive
                        onChange={(e, v) => {
                          if (v == null) return;
                          setPlanForm((f) => ({
                            ...f,
                            permol: { ...(f.permol || defaultPlanPermol()), mode: v },
                          }));
                        }}
                      >
                        <ToggleButton value="kg">Кг</ToggleButton>
                        <ToggleButton value="pct">% від маси</ToggleButton>
                      </ToggleButtonGroup>
                      {planForm.permol?.mode === 'pct' ? (
                        <TextField
                          size="small"
                          label="% від загальної маси замовлення"
                          type="number"
                          value={planForm.permol?.pct ?? ''}
                          onChange={(e) =>
                            setPlanForm((f) => ({
                              ...f,
                              permol: { ...(f.permol || defaultPlanPermol()), pct: e.target.value },
                            }))
                          }
                          inputProps={{ min: 0.01, max: 100, step: 0.1 }}
                          sx={{ width: 220 }}
                        />
                      ) : (
                        <TextField
                          size="small"
                          label="Кількість перемолу (кг)"
                          type="number"
                          value={planForm.permol?.quantity_kg ?? ''}
                          onChange={(e) =>
                            setPlanForm((f) => ({
                              ...f,
                              permol: { ...(f.permol || defaultPlanPermol()), quantity_kg: e.target.value },
                            }))
                          }
                          inputProps={{ min: 0.01, step: 0.01 }}
                          sx={{ width: 200 }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <TextField
              label="Дата і час початку (план)"
              type="datetime-local"
              value={planForm.planned_start}
              onChange={(e) => setPlanForm((f) => ({ ...f, planned_start: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Коментар"
              multiline
              value={planForm.comment}
              onChange={(e) => setPlanForm((f) => ({ ...f, comment: e.target.value }))}
              fullWidth
            />
            {(() => {
              const planProduct = products.find((p) => p.id === Number(planForm.product));
              // packaging_components з виробу: [{packaging_material, packaging_material_name, quantity, ...}]
              const prodPackComps = planProduct?.packaging_components || [];
              // mapping packaging_material id → кількість виробів на одиницю пакування
              const compByPmId = {};
              prodPackComps.forEach((c) => { compByPmId[Number(c.packaging_material)] = c; });
              (planForm.packaging_rows || []).forEach((r) => {
                const pm = Number(r.packaging_material || r._pm_id);
                if (pm && !compByPmId[pm]) {
                  compByPmId[pm] = { packaging_material: pm, packaging_material_name: r.name || '', quantity: 1 };
                }
              });
              // фільтруємо packagingStock лише по виробу (+ вже обрані позиції)
              const filteredPackStockBase = Object.keys(compByPmId).length > 0
                ? packagingStock.filter((s) => s.packaging_material != null && compByPmId[Number(s.packaging_material)])
                : packagingStock;
              const selectedPackIds = new Set(
                (planForm.packaging_rows || []).map((r) => Number(r.stock_item)).filter((id) => Number.isFinite(id) && id > 0)
              );
              const filteredPackStock = (() => {
                const byId = new Map(filteredPackStockBase.map((s) => [Number(s.id), s]));
                packagingStock.forEach((s) => {
                  if (selectedPackIds.has(Number(s.id))) byId.set(Number(s.id), s);
                });
                return Array.from(byId.values());
              })();
              const orderQty = Number(planForm.quantity) || 0;
              // розподілена кількість виробів по рядках
              const allocatedUnits = (planForm.packaging_rows || []).reduce(
                (sum, r) => sum + (Number(r.units_to_pack) || 0), 0
              );
              const remainingUnits = orderQty - allocatedUnits;

              return (
                <Box>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                    Пакування (зі складу «Упаковка»)
                  </Typography>
                  {(planForm.packaging_rows || []).map((row, idx) => {
                    const stockItem = filteredPackStock.find((x) => Number(x.id) === Number(row.stock_item));
                    const pmKey = Number(row._pm_id || row.packaging_material || stockItem?.packaging_material);
                    const comp = (pmKey && compByPmId[pmKey]) || (stockItem ? compByPmId[Number(stockItem.packaging_material)] : null);
                    const unitsPack = comp ? Number(comp.quantity) : null; // виробів на 1 пакування
                    const unitsForRow = Number(row.units_to_pack) || 0;
                    const enteredQty = Number(row.quantity) || 0;
                    const available = stockItem?.quantity ?? null;
                    const hasShortage = available !== null && enteredQty > 0 && enteredQty > available;
                    const displayName = stockItem?.packaging_material_name || row.name || 'Упаковка';
                    const selectValue = row.stock_item != null && row.stock_item !== '' ? Number(row.stock_item) : '';
                    return (
                      <Box key={idx} sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: hasShortage || row.no_stock ? 'error.main' : 'divider', borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'nowrap' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={row.pack_at_machine !== false}
                                onChange={(e) => {
                                  const next = [...(planForm.packaging_rows || [])];
                                  next[idx] = { ...next[idx], pack_at_machine: e.target.checked };
                                  setPlanForm((f) => ({ ...f, packaging_rows: next }));
                                }}
                              />
                            }
                            label={<Typography variant="body2" noWrap>Біля ТПА</Typography>}
                            sx={{ mr: 0, minWidth: 100, flexShrink: 0 }}
                          />
                          {row.no_stock ? (
                            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                              <TextField size="small" label="Упаковка" value={displayName} fullWidth InputProps={{ readOnly: true }} />
                              <Typography variant="caption" color="error.main">Немає на складі «Упаковка»</Typography>
                            </Box>
                          ) : (
                            <TextField
                              select
                              size="small"
                              label="Упаковка"
                              value={selectValue}
                              onChange={(e) => {
                                const val = e.target.value !== '' ? Number(e.target.value) : '';
                                const item = filteredPackStock.find((x) => Number(x.id) === Number(val));
                                const c = item ? compByPmId[Number(item.packaging_material)] : null;
                                const utp = unitsForRow || remainingUnits + unitsForRow;
                                const autoQty = c && utp > 0 ? Math.ceil(utp / Number(c.quantity)) : '';
                                const next = [...(planForm.packaging_rows || [])];
                                next[idx] = {
                                  ...next[idx],
                                  stock_item: val === '' ? null : val,
                                  packaging_material: item?.packaging_material ?? null,
                                  _pm_id: item?.packaging_material ?? next[idx]._pm_id,
                                  name: item?.packaging_material_name || '',
                                  unit: item?.unit || '',
                                  quantity: String(autoQty),
                                };
                                setPlanForm((f) => ({ ...f, packaging_rows: next }));
                              }}
                              sx={{ flex: '1 1 180px', minWidth: 0 }}
                            >
                              <MenuItem value="">— обрати —</MenuItem>
                              {filteredPackStock.map((x) => (
                                <MenuItem key={x.id} value={Number(x.id)}>
                                  {x.packaging_material_name || 'Упаковка'}
                                  {x.packaging_material_type ? ` — ${x.packaging_material_type}` : ''}{' '}
                                  ({x.quantity != null ? `${x.quantity} ${x.unit || 'шт'} на складі` : 'немає'})
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                          {unitsPack && (
                            <TextField
                              size="small"
                              type="number"
                              label="Виробів"
                              value={row.units_to_pack ?? ''}
                              onChange={(e) => {
                                const utp = Number(e.target.value) || 0;
                                const autoQty = unitsPack && utp > 0 ? Math.ceil(utp / unitsPack) : '';
                                const next = [...(planForm.packaging_rows || [])];
                                next[idx] = { ...next[idx], units_to_pack: e.target.value, quantity: String(autoQty) };
                                setPlanForm((f) => ({ ...f, packaging_rows: next }));
                              }}
                              inputProps={{ min: 1, step: 1, max: orderQty }}
                              sx={{ width: 100, flexShrink: 0 }}
                              helperText={`${unitsPack} шт/уп.`}
                            />
                          )}
                          <TextField
                            size="small"
                            type="number"
                            label="Упаковок"
                            value={row.quantity ?? ''}
                            onChange={(e) => {
                              const next = [...(planForm.packaging_rows || [])];
                              next[idx] = { ...next[idx], quantity: e.target.value };
                              setPlanForm((f) => ({ ...f, packaging_rows: next }));
                            }}
                            inputProps={{ min: 0, step: 1 }}
                            sx={{ width: 110, flexShrink: 0 }}
                            error={hasShortage}
                            helperText={available !== null
                              ? hasShortage ? `Мало! є ${available}` : `є ${available}`
                              : ''}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ flexShrink: 0, mt: 0.5 }}
                            onClick={() => {
                              const next = (planForm.packaging_rows || []).filter((_, i) => i !== idx);
                              setPlanForm((f) => ({ ...f, packaging_rows: next }));
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    );
                  })}
                  {orderQty > 0 && allocatedUnits > 0 && allocatedUnits < orderQty && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
                      Розподілено {allocatedUnits} з {orderQty} виробів — не всі вироби мають упаковку
                    </Typography>
                  )}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      // Авто-додаємо перший доступний матеріал з ненульовим залишком
                      const already = new Set((planForm.packaging_rows || []).map((r) => r.stock_item));
                      const next_stock = filteredPackStock.find((s) => !already.has(s.id)) || null;
                      const c = next_stock ? compByPmId[next_stock.packaging_material] : null;
                      const utp = remainingUnits > 0 ? remainingUnits : (orderQty || '');
                      const autoQty = c && Number(utp) > 0 ? Math.ceil(Number(utp) / c.quantity) : '';
                      const newRow = {
                        stock_item: next_stock ? next_stock.id : null,
                        name: next_stock?.packaging_material_name || '',
                        unit: next_stock?.unit || 'шт',
                        quantity: String(autoQty),
                        units_to_pack: utp > 0 ? String(utp) : '',
                        pack_at_machine: true,
                      };
                      setPlanForm((f) => ({ ...f, packaging_rows: [...(f.packaging_rows || []), newRow] }));
                    }}
                    sx={{ mt: 0.5 }}
                  >
                    Додати упаковку
                  </Button>
                </Box>
              );
            })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPlanOrderOpen(false); setEditingOrderId(null); setPlanAltMaterials({}); }}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmitPlanOrder}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(permolDetailOpen)} onClose={() => setPermolDetailOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Склад перемолу (опис)</DialogTitle>
        <DialogContent>
          {permolDetailOpen && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle1">{permolDetailOpen.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {permolDetailOpen.article || '—'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
                {permolDetailOpen.composition_description?.trim()
                  ? permolDetailOpen.composition_description
                  : 'Опис складу не заповнений (дані з позиції сировини «Перемол»).'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermolDetailOpen(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Станки операторів (Planner) */}
      <Dialog open={operatorMachinesOpen} onClose={() => setOperatorMachinesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Призначити станки операторам</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            У списку лише співробітники з доступом до «Робоче місце оператора». Оберіть станки для кожного — на робочому місці оператор бачитиме лише призначені йому станки та замовлення на них.
          </Typography>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Оператор</TableCell>
                  <TableCell>Станки</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(operatorStaffList || []).map((staff) => (
                  <TableRow key={staff.id}>
                    <TableCell>
                      <Typography fontWeight={500}>{staff.full_name}</Typography>
                      <Typography variant="body2" color="text.secondary">{staff.username}</Typography>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={operatorMachinesAssignments[staff.id] || []}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val.includes('__all__')) {
                              const cur = operatorMachinesAssignments[staff.id] || [];
                              handleOperatorMachinesChange(staff.id, cur.length === machines.length ? [] : machines.map(m => m.id));
                            } else {
                              handleOperatorMachinesChange(staff.id, val);
                            }
                          }}
                          renderValue={(selected) =>
                            selected
                              .map((id) => machines.find((m) => m.id === id)?.identifier || id)
                              .join(', ') || '—'
                          }
                          displayEmpty
                        >
                          <MenuItem value="__all__">
                            <Checkbox
                              checked={(operatorMachinesAssignments[staff.id] || []).length === machines.length}
                              indeterminate={(operatorMachinesAssignments[staff.id] || []).length > 0 && (operatorMachinesAssignments[staff.id] || []).length < machines.length}
                            />
                            <ListItemText primary={<em>Обрати всі</em>} />
                          </MenuItem>
                          {machines.map((m) => (
                            <MenuItem key={m.id} value={m.id}>
                              <Checkbox checked={(operatorMachinesAssignments[staff.id] || []).indexOf(m.id) > -1} />
                              <ListItemText primary={`${m.identifier} — ${m.name || ''}`} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {(!operatorStaffList || operatorStaffList.length === 0) && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Немає співробітників з доступом до «Робоче місце оператора». Надайте право в Налаштування → Ролі або Персонал.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOperatorMachinesOpen(false)}>Скасувати</Button>
          <Button
            variant="contained"
            onClick={handleOperatorMachinesSave}
            disabled={operatorMachinesSaving || !operatorStaffList?.length}
          >
            {operatorMachinesSaving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QC trigger (Planner) */}
      <Dialog open={qcTriggerOpen} onClose={() => setQcTriggerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Контроль якості — ініціювати перевірки</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Оберіть станки з запущеним замовленням. Перевірки зʼявляться в канбані ВКЯ («Контроль якості»).
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="qc-machines-label">Станки</InputLabel>
            <Select
              labelId="qc-machines-label"
              multiple
              value={qcTriggerMachines}
              label="Станки"
              onChange={(e) => setQcTriggerMachines(e.target.value.filter(v => v !== '__all__'))}
              renderValue={(selected) =>
                selected
                  .map((id) => machines.find((m) => m.id === id)?.identifier || id)
                  .join(', ')
              }
            >
              <MenuItem
                value="__all__"
                onClick={(e) => {
                  e.stopPropagation();
                  setQcTriggerMachines(qcTriggerMachines.length === machines.length ? [] : machines.map(m => m.id));
                }}
              >
                <Checkbox
                  checked={qcTriggerMachines.length === machines.length && machines.length > 0}
                  indeterminate={qcTriggerMachines.length > 0 && qcTriggerMachines.length < machines.length}
                />
                <ListItemText primary={<em>Обрати всі</em>} />
              </MenuItem>
              {machines.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  <Checkbox checked={qcTriggerMachines.indexOf(m.id) > -1} />
                  <ListItemText primary={`${m.identifier} — ${m.name || ''}`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="qc-operator-label">Оператор (необовʼязково)</InputLabel>
            <Select
              labelId="qc-operator-label"
              value={qcTriggerOperator}
              label="Оператор (необовʼязково)"
              onChange={(e) => setQcTriggerOperator(e.target.value)}
            >
              <MenuItem value="">— будь-хто —</MenuItem>
              {staffList.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.full_name} ({s.username})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQcTriggerOpen(false)}>Скасувати</Button>
          <Button
            variant="contained"
            onClick={handleQcTriggerSubmit}
            disabled={qcTriggerSubmitting || qcTriggerMachines.length === 0}
          >
            {qcTriggerSubmitting ? 'Створення…' : 'Створити перевірки'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add break dialog */}
      <Dialog open={addBreakOpen} onClose={() => setAddBreakOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Додати перерву</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              select
              label="Станок"
              value={breakForm.machine}
              onChange={(e) => setBreakForm((f) => ({ ...f, machine: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">— обрати —</MenuItem>
              {machines.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.identifier} — {m.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Дата і час з"
              type="datetime-local"
              value={breakForm.start_at}
              onChange={(e) => setBreakForm((f) => ({ ...f, start_at: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Дата і час по"
              type="datetime-local"
              value={breakForm.end_at}
              onChange={(e) => setBreakForm((f) => ({ ...f, end_at: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Причина"
              multiline
              value={breakForm.reason}
              onChange={(e) => setBreakForm((f) => ({ ...f, reason: e.target.value }))}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddBreakOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmitBreak}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create report dialog */}
      <Dialog open={reportOpen} onClose={() => setReportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Створити звіт</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            По замовленнях у роботі вкажіть виготовлено (факт). План підрахується автоматично. Якщо факт більший за план — вкажіть причину. Відходи для дроблення (кг) по кожному замовленню після збереження додаються на склад «Відходи для дроблення» (як при завершенні замовлення); 0 — якщо відходів немає.
          </Typography>
          {reportLines.map((line, idx) => (
            <Box key={line.order} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ width: 260, minWidth: 200 }}>
                <Typography sx={{ fontWeight: 500 }} variant="body2">
                  {line.product_name} x{line.quantity}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Вже виготовлено (факт): {line.already_produced_fact ?? 0}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Залишилось: {Math.max(0, Number(line.quantity ?? 0) - Number(line.already_produced_fact ?? 0))}
                </Typography>
              </Box>
              <TextField
                type="number"
                size="small"
                label="Факт"
                value={line.produced_fact}
                onChange={(e) => {
                  const next = [...reportLines];
                  next[idx] = { ...next[idx], produced_fact: e.target.value };
                  setReportLines(next);
                }}
                sx={{ width: 88 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ pt: 1, maxWidth: 72 }}>
                Після цього: {(() => {
                  const add = line.produced_fact === '' || line.produced_fact == null ? 0 : Number(line.produced_fact);
                  const left = Number(line.quantity ?? 0) - Number(line.already_produced_fact ?? 0);
                  if (!Number.isFinite(left) || !Number.isFinite(add)) return '—';
                  return Math.max(0, left - add);
                })()}
              </Typography>
              <TextField
                type="number"
                size="small"
                label="Відходи дроблення, кг"
                value={line.crushing_waste_kg}
                onChange={(e) => {
                  const next = [...reportLines];
                  next[idx] = { ...next[idx], crushing_waste_kg: e.target.value };
                  setReportLines(next);
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ width: 150 }}
                helperText="0 якщо немає"
                FormHelperTextProps={{ sx: { m: 0, mt: 0.25 } }}
              />
              <TextField
                size="small"
                label="Причина"
                value={line.reason}
                onChange={(e) => {
                  const next = [...reportLines];
                  next[idx] = { ...next[idx], reason: e.target.value };
                  setReportLines(next);
                }}
                sx={{ flex: 1, minWidth: 140 }}
              />
            </Box>
          ))}
          {reportLines.length === 0 && (
            <Typography color="text.secondary">Немає замовлень у роботі для звіту.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmitReport} disabled={!reportLines.length}>
            Зберегти звіт
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start late reason */}
      <Dialog open={startLateOpen} onClose={() => setStartLateOpen(false)}>
        <DialogTitle>Причина запізнення старту</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            fullWidth
            label="Причина"
            value={startLateReason}
            onChange={(e) => setStartLateReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartLateOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleStartLateSubmit}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!packagingWarn} onClose={() => setPackagingWarn(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Нестача тари</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600 }}>
            {packagingWarn?.detail
              || 'Увага! На складі тари менше, ніж потрібно. Запустити виробництво на наявний обсяг?'}
          </Typography>
          {(packagingWarn?.items || []).map((item, i) => {
            const label = typeof item === 'string'
              ? item
              : (item.label || `«${item.name}»: є ${item.available}, потрібно ${item.needed}`);
            return (
              <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>• {label}</Typography>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackagingWarn(null)}>Скасувати</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={async () => {
              const { order, stage, startPayload } = packagingWarn;
              setPackagingWarn(null);
              try {
                if (stage === 'start') {
                  await productionOrderService.start(order.id, {
                    ...(startPayload || {}),
                    force_packaging: true,
                  });
                } else {
                  await productionOrderService.launch(order.id, { force: true, force_packaging: true });
                }
                loadOrders();
              } catch (err) {
                alert(err.response?.data?.detail || err.message);
              }
            }}
          >
            Так, запустити
          </Button>
        </DialogActions>
      </Dialog>

      {/* Завершення замовлення: відходи для дроблення + за потреби причина запізнення */}
      <Dialog open={endLateOpen} onClose={() => setEndLateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {completeOrderLate ? 'Завершення замовлення (запізнення)' : 'Завершення замовлення'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Відходи для дроблення (кг)"
            helperText="Скільки кг відходів відправити на склад дроблення. 0 — якщо відходів немає."
            value={crushingWasteKg}
            onChange={(e) => setCrushingWasteKg(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            type="number"
            label="Невідновлювані відходи (кг)"
            helperText="Відходи, що не підлягають переробці (пил, забруднення тощо). Необов'язково."
            value={nonRecoverableWasteKg}
            onChange={(e) => setNonRecoverableWasteKg(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ mt: 2 }}
          />
          {completeOrderLate && (
            <TextField
              multiline
              fullWidth
              required
              label="Причина запізнення завершення"
              value={endLateReason}
              onChange={(e) => setEndLateReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndLateOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleEndLateSubmit}>
            Завершити замовлення
          </Button>
        </DialogActions>
      </Dialog>

      {/* Setup wastes */}
      <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Наладка — втрати сировини на запуск</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedOrder.product_name} x{selectedOrder.quantity}
            </Typography>
          )}
          {setupWastes.map((w, idx) => (
            <TextField
              key={w.raw_material}
              type="number"
              size="small"
              label={w.raw_material_name}
              value={w.quantity}
              onChange={(e) => {
                const next = [...setupWastes];
                next[idx] = { ...next[idx], quantity: parseFloat(e.target.value) || 0 };
                setSetupWastes(next);
              }}
              fullWidth
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ mb: 1 }}
            />
          ))}
          {setupWastes.length === 0 && selectedOrder && (
            <Typography color="text.secondary">
              Для цього замовлення не вказано сировину для списання на наладку.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetupOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSetupSubmit}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      {/* Немає відкритих перевірок КЯ — пояснення та створення */}
      <Dialog open={qcNoPendingOpen} onClose={() => setQcNoPendingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Контроль якості</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Зараз немає відкритих перевірок для ваших ТПА. Планер може ініціювати контроль через меню «Контроль якості», або
            перевірки зʼявляються автоматично за періодом (якщо налаштовано на ТПА).
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Можна створити перевірку зараз для всіх ваших призначених станків — після цього відкриється форма здачі результату.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setQcNoPendingOpen(false)}>Закрити</Button>
          <Button
            variant="contained"
            onClick={handleCreateQcForMyMachines}
            disabled={qcNoPendingSubmitting || operatorMachineIds.length === 0}
          >
            {qcNoPendingSubmitting ? 'Створення…' : 'Створити перевірку для моїх ТПА'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QC complete (Operator) */}
      <Dialog
        open={Boolean(currentQcCheck) && !isDryingView}
        onClose={() => setCurrentQcCheck(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { border: '3px solid', borderColor: 'primary.main' } }}
      >
        <DialogTitle>Контроль якості</DialogTitle>
        <DialogContent>
          {currentQcCheck && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Станок: <strong>{currentQcCheck.machine_identifier || currentQcCheck.machine_name || `#${currentQcCheck.machine}`}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Заповніть результат перевірки.
              </Typography>
              <TextField
                label="Коментар"
                value={qcCompleteComment}
                onChange={(e) => setQcCompleteComment(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Опишіть результат перевірки..."
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={qcCompletePassed}
                    onChange={(e) => setQcCompletePassed(e.target.checked)}
                    color="primary"
                  />
                }
                label="Контроль пройдено"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCurrentQcCheck(null)} disabled={qcCompleteSubmitting}>
            Закрити
          </Button>
          <Button
            variant="contained"
            onClick={handleQcCompleteSubmit}
            disabled={qcCompleteSubmitting}
          >
            {qcCompleteSubmitting ? 'Збереження…' : 'Зберегти результат'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order details */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Деталі замовлення</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                Основне
              </Typography>
              <Box sx={{ pl: 1, mb: 2 }}>
                <Typography variant="body2">Виріб: {selectedOrder.product_name}, кількість: {selectedOrder.quantity} шт</Typography>
                <Typography variant="body2">Станок: {selectedOrder.machine_identifier} — {selectedOrder.machine_name}</Typography>
                <Typography variant="body2">Прес-форма: {selectedOrder.press_form_identifier}</Typography>
                <Typography variant="body2">
                  Склад надходження: {selectedOrder.destination_warehouse || '—'}
                </Typography>
                <Typography variant="body2">Статус: {STATUS_LABELS[selectedOrder.status] || selectedOrder.status}</Typography>
              </Box>

              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Час (план)</Typography>
              <Box sx={{ pl: 1, mb: 2 }}>
                <Typography variant="body2">Початок: {formatDt(selectedOrder.planned_start)}</Typography>
                <Typography variant="body2">Завершення: {formatDt(selectedOrder.planned_end)}</Typography>
              </Box>

              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Час (факт)</Typography>
              <Box sx={{ pl: 1, mb: 2 }}>
                <Typography variant="body2">Початок: {formatDt(selectedOrder.actual_start) || '—'}</Typography>
                <Typography variant="body2">Наладка: {formatDt(selectedOrder.setup_at) || '—'}</Typography>
                <Typography variant="body2">Запуск: {formatDt(selectedOrder.launched_at) || '—'}</Typography>
                <Typography variant="body2">Завершення: {formatDt(selectedOrder.actual_end) || '—'}</Typography>
              </Box>

              {(selectedOrder.start_late_reason || selectedOrder.end_late_reason) && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Причини</Typography>
                  <Box sx={{ pl: 1, mb: 2 }}>
                    {selectedOrder.start_late_reason && (
                      <Typography variant="body2">Запізнення старту: {selectedOrder.start_late_reason}</Typography>
                    )}
                    {selectedOrder.end_late_reason && (
                      <Typography variant="body2">Запізнення завершення: {selectedOrder.end_late_reason}</Typography>
                    )}
                  </Box>
                </>
              )}

              {(selectedOrder.comment || selectedOrder.packaging_note) && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Коментар та пакування</Typography>
                  <Box sx={{ pl: 1, mb: 2 }}>
                    {selectedOrder.comment && <Typography variant="body2">Коментар: {selectedOrder.comment}</Typography>}
                    {selectedOrder.packaging_note && <Typography variant="body2">Пакування: {selectedOrder.packaging_note}</Typography>}
                  </Box>
                </>
              )}

              {(selectedOrder.order_materials?.length > 0) && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Сировина та барвники</Typography>
                  <Box sx={{ pl: 1, mb: 2 }}>
                    {selectedOrder.order_materials.map((m) => (
                      <Box key={m.id} sx={{ mb: 1 }}>
                        <Typography variant="body2">
                          {m.raw_material_name || m.dye_name}: {m.quantity_required}
                        </Typography>
                        {m.needs_drying && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 0.5 }}>
                            Сушіння: {m.drying_time_minutes != null ? `${m.drying_time_minutes} хв` : '—'} ·{' '}
                            {DRYING_STATUS_LABELS[m.drying_status] || m.drying_status || '—'}
                            {m.drying_deadline_at && <> · дедлайн {formatDt(m.drying_deadline_at)}</>}
                            {m.drying_started_at && <> · поч. {formatDt(m.drying_started_at)}</>}
                            {m.drying_completed_at && <> · кін. {formatDt(m.drying_completed_at)}</>}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {(selectedOrder.raw_material_wastes?.length > 0) && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Втрати сировини на наладку</Typography>
                  <Box sx={{ pl: 1, mb: 2 }}>
                    {selectedOrder.raw_material_wastes.map((w) => (
                      <Typography key={w.id} variant="body2">{w.raw_material_name}: {w.quantity}</Typography>
                    ))}
                  </Box>
                </>
              )}

              {(selectedOrder.produced_plan != null || selectedOrder.produced_fact != null) && (
                <>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Звіт</Typography>
                  <Box sx={{ pl: 1, mb: 2 }}>
                    <Typography variant="body2">Виготовлено (план): {selectedOrder.produced_plan ?? '—'}</Typography>
                    <Typography variant="body2">Виготовлено (факт): {selectedOrder.produced_fact ?? '—'}</Typography>
                    {selectedOrder.report_reason && <Typography variant="body2">Причина: {selectedOrder.report_reason}</Typography>}
                  </Box>
                </>
              )}

              <Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 1 }}>Історія змін (лог)</Typography>
              <Box component="ul" sx={{ pl: 2, mt: 0.5, listStyle: 'none' }}>
                {(selectedOrder.logs || []).map((log) => (
                  <Box component="li" key={log.id} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="medium">
                      {log.action === 'defect_added' ? '⚠ Браковані вироби — план збільшено' :
                       log.action === 'complete' ? '✅ Завершено' :
                       log.action === 'launch' ? '🚀 Запущено' :
                       log.action === 'plan' ? '📋 Заплановано' :
                       log.action} — {formatDt(log.created_at)}
                    </Typography>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <Box sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 0.5, pl: 1 }}>
                        {Object.entries(log.details).map(([key, value]) => (
                          <Typography key={key} variant="body2" component="span" sx={{ display: 'block' }}>
                            {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Попап призначення замовлення (режим оператора) */}
      <Dialog open={Boolean(assignedOrderPopup)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Нове виробниче замовлення
          {pendingAssignments.length > 1 && (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
              ({pendingAssignments.findIndex((o) => o.id === assignedOrderPopup?.id) + 1} з {pendingAssignments.length})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {assignedOrderPopup && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="body1">
                Вам призначено замовлення:
              </Typography>
              <Typography variant="h6" color="primary">
                {assignedOrderPopup.product_name} × {assignedOrderPopup.quantity} шт.
              </Typography>
              <Typography variant="body2">
                Станок: <strong>{assignedOrderPopup.machine_identifier}</strong>
              </Typography>
              <Typography variant="body2">
                Початок (план): <strong>{assignedOrderPopup.planned_start ? new Date(assignedOrderPopup.planned_start).toLocaleString('uk-UA') : '—'}</strong>
              </Typography>
              {declineMode && (
                <TextField
                  label="Причина відмови *"
                  multiline
                  minRows={2}
                  fullWidth
                  value={declineReasonInput}
                  onChange={(e) => setDeclineReasonInput(e.target.value)}
                  autoFocus
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!declineMode ? (
            <>
              <Button color="error" onClick={() => setDeclineMode(true)} disabled={popupSubmitting}>
                Не можу виконати
              </Button>
              <Button variant="contained" onClick={handleOperatorAccept} disabled={popupSubmitting}>
                Взяв в роботу
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setDeclineMode(false)} disabled={popupSubmitting}>Назад</Button>
              <Button color="error" variant="contained" onClick={handleOperatorDecline} disabled={popupSubmitting || !declineReasonInput.trim()}>
                Підтвердити відмову
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Звіт за зміну (режим оператора) */}
      <Dialog open={shiftReportOpen} onClose={() => !shiftReportSubmitting && setShiftReportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Звіт за зміну</DialogTitle>
        <DialogContent dividers>
          {shiftReportDone ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>Звіт подано!</Typography>
              <Typography variant="body2">Бажаєте вийти з системи?</Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                <Button variant="outlined" onClick={() => setShiftReportOpen(false)}>Залишитись</Button>
                <Button variant="contained" color="error" onClick={() => { authService.logout().finally(() => { window.location.href = '/login'; }); }}>Вийти</Button>
              </Box>
            </Box>
          ) : shiftReportLines.length === 0 ? (
            <Typography color="text.secondary">Немає активних замовлень для звіту.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Замовлення {shiftReportStep + 1} з {shiftReportLines.length}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700}>
                {shiftReportLines[shiftReportStep]?.order?.product_name} × {shiftReportLines[shiftReportStep]?.order?.quantity} шт.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Станок: {shiftReportLines[shiftReportStep]?.order?.machine_identifier}
              </Typography>
              <TextField
                label="Кількість виробів (факт) *"
                type="number"
                inputProps={{ min: 0 }}
                value={shiftReportLines[shiftReportStep]?.produced_qty ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setShiftReportLines((prev) => prev.map((l, i) => i === shiftReportStep ? { ...l, produced_qty: v } : l));
                }}
                fullWidth
              />
              <TextField
                label="Вага відходів (кг)"
                type="number"
                inputProps={{ min: 0, step: 0.001 }}
                value={shiftReportLines[shiftReportStep]?.waste_kg ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setShiftReportLines((prev) => prev.map((l, i) => i === shiftReportStep ? { ...l, waste_kg: v } : l));
                }}
                fullWidth
              />
              <TextField
                label="Коментар"
                multiline
                minRows={2}
                value={shiftReportLines[shiftReportStep]?.comment ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setShiftReportLines((prev) => prev.map((l, i) => i === shiftReportStep ? { ...l, comment: v } : l));
                }}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        {!shiftReportDone && shiftReportLines.length > 0 && (
          <DialogActions>
            <Button onClick={() => setShiftReportOpen(false)} disabled={shiftReportSubmitting}>Скасувати</Button>
            {shiftReportStep > 0 && (
              <Button onClick={() => setShiftReportStep((s) => s - 1)} disabled={shiftReportSubmitting}>Назад</Button>
            )}
            {shiftReportStep < shiftReportLines.length - 1 ? (
              <Button
                variant="outlined"
                onClick={() => setShiftReportStep((s) => s + 1)}
                disabled={!shiftReportLines[shiftReportStep]?.produced_qty}
              >
                Далі
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleShiftReportSubmit}
                disabled={shiftReportSubmitting || !shiftReportLines[shiftReportStep]?.produced_qty}
              >
                {shiftReportSubmitting ? 'Збереження…' : 'Подати звіт'}
              </Button>
            )}
          </DialogActions>
        )}
      </Dialog>

      {/* Оператори зміни (режим планера) */}
      <Dialog open={shiftOpsOpen} onClose={() => setShiftOpsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Оператори зміни — непризначені замовлення</DialogTitle>
        <DialogContent dividers>
          {shiftOpsOrders.length === 0 ? (
            <Typography color="text.secondary">Усі активні замовлення мають призначених операторів.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {shiftOpsOrders.map((order) => {
                const machineOps = shiftOpsOperators[order.machine] || [];
                return (
                  <Box key={order.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {order.product_name} × {order.quantity} шт. — {order.machine_identifier}
                    </Typography>
                    <FormControl fullWidth size="small">
                      <InputLabel>Оператор</InputLabel>
                      <Select
                        label="Оператор"
                        value={shiftOpsAssignments[order.id] || ''}
                        onChange={(e) => setShiftOpsAssignments((prev) => ({ ...prev, [order.id]: e.target.value }))}
                      >
                        <MenuItem value=""><em>— не призначено —</em></MenuItem>
                        {machineOps.map((op) => (
                          <MenuItem key={op.id} value={op.id}>{op.full_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {machineOps.length === 0 && (
                      <Typography variant="caption" color="text.secondary">На цей ТПА не призначено операторів</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftOpsOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleShiftOpsSave} disabled={shiftOpsSaving || shiftOpsOrders.length === 0}>
            {shiftOpsSaving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
